import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, MoreHorizontal, ChevronDown, Search, Star, Info } from 'lucide-react'
import Layout from '../../components/Layout/Layout'
import TradingChart from '../../components/TradingChart'
import SettingsSidebar from '../../components/ui/SettingsSidebar'
import { openTrade, getMyOpenTrades, closeTrade, getMarketPrices, getDetailedMarketData } from '../../services/tradeService'
import { getBalance, getCachedBalance, setCachedBalance, addSpotHolding, deductSpotHolding, getSpotHolding, syncSpotHoldingsFromBackend } from '../../services/walletService'
import { formatAmount } from '../../lib/utils'
import { useToast } from '../../context/ToastContext'
import '../../styles/dashboard.css'
import '../../styles/trade.css'
import '../../styles/spot-trade.css'

const PAIR_SYMBOLS = [
    'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT', 'XRP/USDT',
    'DOGE/USDT', 'ADA/USDT', 'AVAX/USDT', 'DOT/USDT', 'LINK/USDT',
    'LTC/USDT', 'NEAR/USDT', 'APT/USDT', 'ATOM/USDT', 'UNI/USDT',
    'TRX/USDT', 'TON/USDT', 'SHIB/USDT', 'BCH/USDT', 'ICP/USDT',
    'FIL/USDT', 'HBAR/USDT', 'VET/USDT', 'OP/USDT', 'ARB/USDT',
    'MKR/USDT', 'AAVE/USDT', 'GRT/USDT', 'STX/USDT', 'INJ/USDT',
    'IMX/USDT', 'ALGO/USDT', 'ETC/USDT', 'EGLD/USDT', 'SAND/USDT',
    'MANA/USDT', 'AXS/USDT', 'THETA/USDT', 'FTM/USDT', 'RUNE/USDT',
    'KAS/USDT', 'SUI/USDT', 'SEI/USDT', 'TIA/USDT', 'JUP/USDT',
    'WIF/USDT', 'BONK/USDT', 'PEPE/USDT', 'FLOKI/USDT', 'NOT/USDT',
    'RENDER/USDT', 'FET/USDT', 'AGIX/USDT', 'OCEAN/USDT', 'WLD/USDT',
    'CFX/USDT', 'ROSE/USDT', 'ZIL/USDT', 'ONE/USDT', 'HOT/USDT',
    'ENJ/USDT', 'CHZ/USDT', 'GALA/USDT', 'GMT/USDT', 'MAGIC/USDT',
    'BLUR/USDT', 'DYDX/USDT', 'SNX/USDT', 'LRC/USDT', 'BAT/USDT',
    'ZRX/USDT', 'COMP/USDT', 'YFI/USDT', 'CRV/USDT', 'CVX/USDT',
    // --- New pairs added (client instrument expansion) ---
    '1INCH/USDT',
    'ACE/USDT', 'ACH/USDT', 'AERGO/USDT', 'AEVO/USDT', 'AGLD/USDT',
    'AIXBT/USDT', 'ALICE/USDT', 'ALPACA/USDT',
    'ALPHA/USDT', 'ALT/USDT', 'AMB/USDT', 'ANIME/USDT', 'ANKR/USDT',
    'APE/USDT', 'API3/USDT', 'AR/USDT', 'ARC/USDT', 'ARK/USDT',
    'ARKM/USDT', 'ASR/USDT', 'ASTR/USDT', 'ATA/USDT', 'AUCTION/USDT',
    'AUDIO/USDT', 'AURA/USDT', 'BAL/USDT', 'BANANA/USDT', 'BAND/USDT',
    'BB/USDT', 'BEL/USDT', 'BERA/USDT', 'BETA/USDT', 'BICO/USDT',
    'BIFI/USDT', 'BIO/USDT', 'BLAST/USDT', 'BLZ/USDT', 'BNT/USDT',
    'BOME/USDT', 'BSW/USDT', 'C98/USDT', 'CAKE/USDT',
    'CAT/USDT', 'CATI/USDT', 'CELO/USDT', 'CELR/USDT', 'CET/USDT',
    'CGPT/USDT', 'CHR/USDT', 'CKB/USDT', 'COMBO/USDT', 'COOKIE/USDT',
    'COS/USDT', 'COTI/USDT', 'CTSI/USDT', 'CVC/USDT', 'CYBER/USDT',
    'DAR/USDT', 'DASH/USDT', 'DATA/USDT', 'DEGO/USDT', 'DENT/USDT',
    'DEXE/USDT', 'DGB/USDT', 'DIA/USDT', 'DODO/USDT', 'DOGS/USDT',
    'DUSK/USDT', 'DYM/USDT', 'EDU/USDT', 'EIGEN/USDT',
    'ELF/USDT', 'ENA/USDT', 'ENS/USDT', 'EOS/USDT', 'ETHFI/USDT',
    'ETHW/USDT', 'FIDA/USDT', 'FIO/USDT', 'FLM/USDT', 'FLOW/USDT',
    'FORTH/USDT', 'FRONT/USDT', 'FUEL/USDT', 'FXS/USDT', 'GAS/USDT',
    'GHST/USDT', 'GLM/USDT', 'GLMR/USDT', 'GMX/USDT', 'GPS/USDT',
    'GRASS/USDT', 'HIFI/USDT', 'HIGH/USDT', 'HIPPO/USDT', 'HIVE/USDT',
    'HMSTR/USDT', 'HOOK/USDT', 'HYPE/USDT', 'ICX/USDT', 'ID/USDT',
    'IDEX/USDT', 'ILV/USDT', 'INIT/USDT', 'IO/USDT', 'IOST/USDT',
    'IOTA/USDT', 'IOTX/USDT', 'IQ/USDT', 'JASMY/USDT', 'JOE/USDT',
    'JTO/USDT', 'KAITO/USDT', 'KAVA/USDT', 'KEY/USDT', 'KNC/USDT',
    'KSM/USDT', 'LAZIO/USDT', 'LDO/USDT', 'LEVER/USDT', 'LINA/USDT',
    'LISTA/USDT', 'LOKA/USDT', 'LPT/USDT', 'LQTY/USDT',
    'LSK/USDT', 'LTO/USDT', 'LUMIA/USDT', 'MANTA/USDT', 'MASK/USDT',
    'MATIC/USDT', 'MBOX/USDT', 'MDT/USDT', 'ME/USDT', 'MEME/USDT',
    'MERL/USDT', 'METIS/USDT', 'MEW/USDT', 'MINA/USDT', 'MOVE/USDT',
    'MTL/USDT', 'MYRO/USDT', 'NEIRO/USDT', 'NEO/USDT', 'NFP/USDT',
    'NIL/USDT', 'NKN/USDT', 'NMR/USDT', 'NTRN/USDT', 'OG/USDT',
    'OGN/USDT', 'OM/USDT', 'OMG/USDT', 'ONDO/USDT', 'ONT/USDT',
    'ORCA/USDT', 'ORDI/USDT', 'OSMO/USDT', 'PENDLE/USDT', 'PENGU/USDT',
    'PHA/USDT', 'PHB/USDT', 'PIXEL/USDT', 'POL/USDT', 'PONKE/USDT',
    'POPCAT/USDT', 'PORTAL/USDT', 'PROM/USDT', 'PUNDIX/USDT', 'PYTH/USDT',
    'QTUM/USDT', 'QUICK/USDT', 'RAD/USDT', 'RARE/USDT', 'RAY/USDT',
    'RED/USDT', 'REEF/USDT', 'REN/USDT', 'REZ/USDT', 'RLC/USDT',
    'RSR/USDT', 'RVN/USDT', 'SAGA/USDT', 'SCR/USDT', 'SFP/USDT',
    'SIREN/USDT', 'SKL/USDT', 'SLP/USDT', 'SPELL/USDT', 'SSV/USDT',
    'STEEM/USDT', 'STG/USDT', 'STORJ/USDT', 'STRK/USDT', 'SUN/USDT',
    'SUPER/USDT', 'SUSHI/USDT', 'SWARMS/USDT', 'SXP/USDT', 'SYN/USDT',
    'SYS/USDT', 'TAO/USDT', 'TLM/USDT', 'TNSR/USDT', 'TOKEN/USDT',
    'TRB/USDT', 'TROY/USDT', 'TURBO/USDT', 'TWT/USDT', 'UMA/USDT',
    'USTC/USDT', 'VANA/USDT', 'VELO/USDT', 'VIRTUAL/USDT',
    'VOXEL/USDT', 'WAN/USDT', 'WOO/USDT', 'XAI/USDT', 'XEC/USDT',
    'XLM/USDT', 'XMR/USDT', 'XTZ/USDT', 'XVG/USDT', 'YGG/USDT',
    'ZEC/USDT', 'ZEN/USDT', 'ZETA/USDT', 'ZEUS/USDT', 'ZIG/USDT',
    'ZK/USDT', 'ZRO/USDT',
]

const COIN_NAMES: Record<string, string> = {
    BTC: 'Bitcoin', ETH: 'Ethereum', SOL: 'Solana', BNB: 'BNB', XRP: 'Ripple',
    DOGE: 'Dogecoin', ADA: 'Cardano', AVAX: 'Avalanche', DOT: 'Polkadot',
    LINK: 'Chainlink', LTC: 'Litecoin', NEAR: 'NEAR Protocol', APT: 'Aptos',
    ATOM: 'Cosmos', UNI: 'Uniswap', TRX: 'TRON', TON: 'Toncoin',
    SHIB: 'Shiba Inu', BCH: 'Bitcoin Cash', ICP: 'Internet Computer',
    FIL: 'Filecoin', HBAR: 'Hedera', VET: 'VeChain', OP: 'Optimism',
    ARB: 'Arbitrum', MKR: 'Maker', AAVE: 'Aave', GRT: 'The Graph',
    STX: 'Stacks', INJ: 'Injective', IMX: 'Immutable', ALGO: 'Algorand',
    ETC: 'Ethereum Classic', EGLD: 'MultiversX', SAND: 'The Sandbox',
    MANA: 'Decentraland', AXS: 'Axie Infinity', THETA: 'Theta Network',
    FTM: 'Fantom', RUNE: 'THORChain', KAS: 'Kaspa', SUI: 'Sui',
    SEI: 'Sei', TIA: 'Celestia', JUP: 'Jupiter', WIF: 'dogwifhat',
    BONK: 'Bonk', PEPE: 'Pepe', FLOKI: 'Floki', NOT: 'Notcoin',
    RENDER: 'Render', FET: 'Fetch.ai', AGIX: 'SingularityNET',
    OCEAN: 'Ocean Protocol', WLD: 'Worldcoin', CFX: 'Conflux',
    ROSE: 'Oasis Network', ZIL: 'Zilliqa', ONE: 'Harmony', HOT: 'Holo',
    ENJ: 'Enjin Coin', CHZ: 'Chiliz', GALA: 'Gala', GMT: 'STEPN',
    MAGIC: 'Magic', BLUR: 'Blur', DYDX: 'dYdX', SNX: 'Synthetix',
    LRC: 'Loopring', BAT: 'Basic Attention Token', ZRX: '0x Protocol',
    COMP: 'Compound', YFI: 'yearn.finance', CRV: 'Curve DAO', CVX: 'Convex Finance',
    // --- New pairs added (client instrument expansion) ---
    '1INCH': '1inch Network', ACE: 'Fusionist', ACH: 'Alchemy Pay', AERGO: 'Aergo',
    AEVO: 'Aevo', AGLD: 'Adventure Gold', AIXBT: 'aixbt by Virtuals',
    ALICE: 'MyNeighborAlice', ALPACA: 'Alpaca Finance', ALPHA: 'Stella',
    ALT: 'AltLayer', AMB: 'AirDAO', ANIME: 'Anime', ANKR: 'Ankr',
    APE: 'ApeCoin', API3: 'API3', AR: 'Arweave', ARC: 'AI Rig Complex',
    ARK: 'Ark', ARKM: 'Arkham', ASR: 'AS Roma Fan Token', ASTR: 'Astar',
    ATA: 'Automata Network', AUCTION: 'Bounce', AUDIO: 'Audius', AURA: 'Aura Finance',
    BAL: 'Balancer', BANANA: 'Banana Gun', BAND: 'Band Protocol', BB: 'BounceBit',
    BEL: 'Bella Protocol', BERA: 'Berachain', BETA: 'Beta Finance', BICO: 'Biconomy',
    BIFI: 'Beefy Finance', BIO: 'Bio Protocol', BLAST: 'Blast', BLZ: 'Bluzelle',
    BNT: 'Bancor', BOME: 'Book of Meme', BSW: 'Biswap',
    C98: 'Coin98', CAKE: 'PancakeSwap', CAT: "Simon's Cat", CATI: 'Catizen',
    CELO: 'Celo', CELR: 'Celer Network', CET: 'CoinEx Token', CGPT: 'ChainGPT',
    CHR: 'Chromia', CKB: 'Nervos Network', COMBO: 'Furucombo', COOKIE: 'Cookie DAO',
    COS: 'Contentos', COTI: 'COTI', CTSI: 'Cartesi', CVC: 'Civic',
    CYBER: 'CyberConnect', DAR: 'Mines of Dalarnia', DASH: 'Dash', DATA: 'Streamr',
    DEGO: 'Dego Finance', DENT: 'Dent', DEXE: 'DeXe', DGB: 'DigiByte',
    DIA: 'DIA', DODO: 'DODO', DOGS: 'Dogs', DUSK: 'Dusk Network',
    DYM: 'Dymension', EDU: 'Open Campus', EIGEN: 'EigenLayer',
    ELF: 'aelf', ENA: 'Ethena', ENS: 'Ethereum Name Service', EOS: 'EOS',
    ETHFI: 'Ether.fi', ETHW: 'EthereumPoW', FIDA: 'Solana Name Service (Fida)', FIO: 'FIO Protocol',
    FLM: 'Flamingo', FLOW: 'Flow', FORTH: 'Ampleforth Governance', FRONT: 'Frontier',
    FUEL: 'Fuel Network', FXS: 'Frax Share', GAS: 'Gas (Neo)', GHST: 'Aavegotchi',
    GLM: 'Golem', GLMR: 'Moonbeam', GMX: 'GMX', GPS: 'WorkQuest',
    GRASS: 'Grass', HIFI: 'Hifi Finance', HIGH: 'Highstreet', HIPPO: 'Hippo',
    HIVE: 'Hive', HMSTR: 'Hamster Kombat', HOOK: 'Hooked Protocol', HYPE: 'Hyperliquid',
    ICX: 'ICON', ID: 'Space ID', IDEX: 'IDEX', ILV: 'Illuvium',
    INIT: 'Initia', IO: 'io.net', IOST: 'IOST', IOTA: 'IOTA',
    IOTX: 'IoTeX', IQ: 'IQ', JASMY: 'JasmyCoin', JOE: 'JOE (Trader Joe)',
    JTO: 'Jito', KAITO: 'Kaito', KAVA: 'Kava', KEY: 'SelfKey',
    KNC: 'Kyber Network Crystal', KSM: 'Kusama', LAZIO: 'S.S. Lazio Fan Token', LDO: 'Lido DAO',
    LEVER: 'LeverFi', LINA: 'Linear Finance', LISTA: 'Lista DAO', LOKA: 'League of Kingdoms',
    LPT: 'Livepeer', LQTY: 'Liquity', LSK: 'Lisk',
    LTO: 'LTO Network', LUMIA: 'Lumia', MANTA: 'Manta Network', MASK: 'Mask Network',
    MATIC: 'Polygon', MBOX: 'MOBOX', MDT: 'Measurable Data Token', ME: 'Magic Eden',
    MEME: 'Memecoin', MERL: 'Merlin Chain', METIS: 'Metis', MEW: 'cat in a dogs world',
    MINA: 'Mina Protocol', MOVE: 'Movement', MTL: 'Metal DAO', MYRO: 'Myro',
    NEIRO: 'Neiro', NEO: 'NEO', NFP: 'NFPrompt', NIL: 'Nil Foundation',
    NKN: 'NKN', NMR: 'Numeraire', NTRN: 'Neutron', OG: 'OG Fan Token',
    OGN: 'Origin Protocol', OM: 'MANTRA', OMG: 'OMG Network', ONDO: 'Ondo Finance',
    ONT: 'Ontology', ORCA: 'Orca', ORDI: 'ORDI', OSMO: 'Osmosis',
    PENDLE: 'Pendle', PENGU: 'Pudgy Penguins', PHA: 'Phala Network', PHB: 'Phoenix',
    PIXEL: 'Pixels', POL: 'Polygon Ecosystem Token', PONKE: 'Ponke', POPCAT: 'Popcat',
    PORTAL: 'Portal', PROM: 'Prom', PUNDIX: 'Pundi X', PYTH: 'Pyth Network',
    QTUM: 'Qtum', QUICK: 'QuickSwap', RAD: 'Radworks', RARE: 'SuperRare',
    RAY: 'Raydium', RED: 'Redstone', REEF: 'Reef', REN: 'Ren',
    REZ: 'Renzo', RLC: 'iExec RLC', RSR: 'Reserve Rights', RVN: 'Ravencoin',
    SAGA: 'Saga', SCR: 'Scroll', SFP: 'SafePal', SIREN: 'Siren',
    SKL: 'SKALE Network', SLP: 'Smooth Love Potion', SPELL: 'Spell Token', SSV: 'ssv.network',
    STEEM: 'Steem', STG: 'Stargate Finance', STORJ: 'Storj', STRK: 'Starknet',
    SUN: 'Sun Token', SUPER: 'SuperVerse', SUSHI: 'SushiSwap', SWARMS: 'Swarms',
    SXP: 'Solar', SYN: 'Synapse', SYS: 'Syscoin', TAO: 'Bittensor',
    TLM: 'Alien Worlds', TNSR: 'Tensor', TOKEN: 'TokenFi', TRB: 'Tellor',
    TROY: 'Troy', TURBO: 'Turbo', TWT: 'Trust Wallet Token', UMA: 'UMA',
    USTC: 'TerraClassicUSD', VANA: 'Vana', VELO: 'Velodrome Finance',
    VIRTUAL: 'Virtuals Protocol', VOXEL: 'Voxies', WAN: 'Wanchain', WOO: 'WOO Network',
    XAI: 'Xai', XEC: 'eCash', XLM: 'Stellar', XMR: 'Monero',
    XTZ: 'Tezos', XVG: 'Verge', YGG: 'Yield Guild Games', ZEC: 'Zcash',
    ZEN: 'Horizen', ZETA: 'ZetaChain', ZEUS: 'Zeus Network', ZIG: 'ZIGChain',
    ZK: 'ZKsync', ZRO: 'LayerZero',
}

const toBinanceSymbol = (pair: string) => pair.replace('/', '')

/**
 * Format a live price with smart precision:
 *   >= $1000 → 2 decimal places  (BTC: 63,412.58)
 *   >= $1    → 4 decimal places  (XRP: 1.1445)
 *   >= $0.01 → 6 decimal places  (SHIB: 0.000018)
 *   < $0.01  → 8 decimal places  (very small tokens)
 */
const formatLivePrice = (price: number): string => {
    if (!price || price <= 0) return '0.00'
    if (price >= 1000) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    if (price >= 1)    return price.toFixed(4)
    if (price >= 0.01) return price.toFixed(6)
    return price.toFixed(8)
}

// Crypto coin logos from spothq/cryptocurrency-icons (32px color PNGs)
// ── Logo helpers ────────────────────────────────────────────────────────────
// jsDelivr mirrors spothq/cryptocurrency-icons with proper CDN caching
const coinLogo = (symbol: string) =>
    `https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/32/color/${symbol.toLowerCase()}.png`
// CoinGecko thumb is used as second-chance fallback
const coinLogoFallback = (symbol: string) =>
    `https://assets.coingecko.com/coins/images/1/thumb/${symbol.toLowerCase()}.png`
// Twelve Data stock logos (no-auth, CORS-friendly)
const stockLogo = (symbol: string) => `https://logo.twelvedata.com/${symbol.toLowerCase()}`

export const STOCK_PAIRS = [
    { symbol: 'AAPL', name: 'Apple Inc.', yahooSymbol: 'AAPL' },
    { symbol: 'MSFT', name: 'Microsoft', yahooSymbol: 'MSFT' },
    { symbol: 'NVDA', name: 'NVIDIA', yahooSymbol: 'NVDA' },
    { symbol: 'GOOGL', name: 'Alphabet', yahooSymbol: 'GOOGL' },
    { symbol: 'AMZN', name: 'Amazon', yahooSymbol: 'AMZN' },
    { symbol: 'TSLA', name: 'Tesla', yahooSymbol: 'TSLA' },
    { symbol: 'META', name: 'Meta', yahooSymbol: 'META' },
    { symbol: 'NFLX', name: 'Netflix', yahooSymbol: 'NFLX' },
    { symbol: 'JPM', name: 'JPMorgan', yahooSymbol: 'JPM' },
    { symbol: 'V', name: 'Visa', yahooSymbol: 'V' },
    { symbol: 'MA', name: 'Mastercard', yahooSymbol: 'MA' },
    { symbol: 'WMT', name: 'Walmart', yahooSymbol: 'WMT' },
    { symbol: 'DIS', name: 'Disney', yahooSymbol: 'DIS' },
    { symbol: 'AMD', name: 'AMD', yahooSymbol: 'AMD' },
    { symbol: 'INTC', name: 'Intel', yahooSymbol: 'INTC' },
    { symbol: 'COIN', name: 'Coinbase', yahooSymbol: 'COIN' },
    { symbol: 'PYPL', name: 'PayPal', yahooSymbol: 'PYPL' },
    { symbol: 'UBER', name: 'Uber', yahooSymbol: 'UBER' },
    { symbol: 'ORCL', name: 'Oracle', yahooSymbol: 'ORCL' },
    { symbol: 'CRM', name: 'Salesforce', yahooSymbol: 'CRM' },
    { symbol: 'BABA', name: 'Alibaba', yahooSymbol: 'BABA' },
    { symbol: 'GS', name: 'Goldman Sachs', yahooSymbol: 'GS' },
    { symbol: 'MS', name: 'Morgan Stanley', yahooSymbol: 'MS' },
    { symbol: 'BAC', name: 'Bank of America', yahooSymbol: 'BAC' },
    { symbol: 'XOM', name: 'ExxonMobil', yahooSymbol: 'XOM' },
    { symbol: 'CVX', name: 'Chevron', yahooSymbol: 'CVX' },
    { symbol: 'NKE', name: 'Nike', yahooSymbol: 'NKE' },
    { symbol: 'KO', name: 'Coca-Cola', yahooSymbol: 'KO' },
    { symbol: 'PEP', name: 'PepsiCo', yahooSymbol: 'PEP' },
    { symbol: 'MCD', name: "McDonald's", yahooSymbol: 'MCD' },
    { symbol: 'SBUX', name: 'Starbucks', yahooSymbol: 'SBUX' },
    { symbol: 'JNJ', name: 'Johnson & Johnson', yahooSymbol: 'JNJ' },
    { symbol: 'PG', name: 'Procter & Gamble', yahooSymbol: 'PG' },
    { symbol: 'BA', name: 'Boeing', yahooSymbol: 'BA' },
    { symbol: 'TSM', name: 'TSMC', yahooSymbol: 'TSM' },
    { symbol: 'ASML', name: 'ASML Holding', yahooSymbol: 'ASML' },
    { symbol: 'SHOP', name: 'Shopify', yahooSymbol: 'SHOP' },
    { symbol: 'ABNB', name: 'Airbnb', yahooSymbol: 'ABNB' },
    { symbol: 'PLTR', name: 'Palantir', yahooSymbol: 'PLTR' },
    { symbol: 'SNOW', name: 'Snowflake', yahooSymbol: 'SNOW' },
    { symbol: 'SPOT', name: 'Spotify', yahooSymbol: 'SPOT' },
    { symbol: 'SQ', name: 'Block Inc.', yahooSymbol: 'SQ' },
    { symbol: 'HOOD', name: 'Robinhood', yahooSymbol: 'HOOD' },
    { symbol: 'MSTR', name: 'MicroStrategy', yahooSymbol: 'MSTR' },
    { symbol: 'ARM', name: 'Arm Holdings', yahooSymbol: 'ARM' },
    { symbol: 'AVGO', name: 'Broadcom', yahooSymbol: 'AVGO' },
    { symbol: 'QCOM', name: 'Qualcomm', yahooSymbol: 'QCOM' },
    { symbol: 'MU', name: 'Micron Tech', yahooSymbol: 'MU' },
    { symbol: 'ADBE', name: 'Adobe', yahooSymbol: 'ADBE' },
    { symbol: 'NOW', name: 'ServiceNow', yahooSymbol: 'NOW' },
    { symbol: 'RBLX', name: 'Roblox', yahooSymbol: 'RBLX' },
    { symbol: 'ARAMCO', name: 'Saudi Aramco', yahooSymbol: '2222.SR' },
    { symbol: 'RTX', name: 'RTX Corporation', yahooSymbol: 'RTX' },
    { symbol: 'BRK-B', name: 'Berkshire Hathaway', yahooSymbol: 'BRK-B' },
    { symbol: 'LLY', name: 'Eli Lilly', yahooSymbol: 'LLY' },
    { symbol: 'UNH', name: 'UnitedHealth', yahooSymbol: 'UNH' },
    { symbol: 'HD', name: 'Home Depot', yahooSymbol: 'HD' },
    { symbol: 'COST', name: 'Costco', yahooSymbol: 'COST' },
    { symbol: 'ABBV', name: 'AbbVie', yahooSymbol: 'ABBV' },
    { symbol: 'MRK', name: 'Merck & Co.', yahooSymbol: 'MRK' },
    { symbol: 'TMO', name: 'Thermo Fisher', yahooSymbol: 'TMO' },
    { symbol: 'CSCO', name: 'Cisco', yahooSymbol: 'CSCO' },
    { symbol: 'TMUS', name: 'T-Mobile US', yahooSymbol: 'TMUS' },
    { symbol: 'TXN', name: 'Texas Instruments', yahooSymbol: 'TXN' },
    { symbol: 'NVO', name: 'Novo Nordisk', yahooSymbol: 'NVO' },
    { symbol: 'TM', name: 'Toyota', yahooSymbol: 'TM' },
    { symbol: 'SAP', name: 'SAP SE', yahooSymbol: 'SAP' },
    { symbol: 'SONY', name: 'Sony Group', yahooSymbol: 'SONY' },
    { symbol: 'BHP', name: 'BHP Group', yahooSymbol: 'BHP' },
    { symbol: 'LMT', name: 'Lockheed Martin', yahooSymbol: 'LMT' },
    { symbol: 'CAT', name: 'Caterpillar', yahooSymbol: 'CAT' },
]

export const COMMODITY_PAIRS = [
    { symbol: 'GOLD', name: 'Gold', yahooSymbol: 'GC=F', color: '#F5C518', emoji: '🥇' },
    { symbol: 'SILVER', name: 'Silver', yahooSymbol: 'SI=F', color: '#C0C0C0', emoji: '🥈' },
    { symbol: 'OIL', name: 'Crude Oil', yahooSymbol: 'CL=F', color: '#1a1a2e', emoji: '🛢️' },
    { symbol: 'NATGAS', name: 'Natural Gas', yahooSymbol: 'NG=F', color: '#4299e1', emoji: '🔥' },
    { symbol: 'WHEAT', name: 'Wheat', yahooSymbol: 'ZW=F', color: '#d97706', emoji: '🌾' },
    { symbol: 'CORN', name: 'Corn', yahooSymbol: 'ZC=F', color: '#eab308', emoji: '🌽' },
    { symbol: 'COPPER', name: 'Copper', yahooSymbol: 'HG=F', color: '#b45309', emoji: '🔶' },
    { symbol: 'PLAT', name: 'Platinum', yahooSymbol: 'PL=F', color: '#94a3b8', emoji: '💎' },
    { symbol: 'COFFEE', name: 'Coffee', yahooSymbol: 'KC=F', color: '#78350f', emoji: '☕' },
    { symbol: 'SUGAR', name: 'Sugar', yahooSymbol: 'SB=F', color: '#fce7f3', emoji: '🍬' },
    // New commodities (client list expansion) — live via Yahoo Finance
    { symbol: 'PALLADIUM', name: 'Palladium', yahooSymbol: 'PA=F', color: '#94a3b8', emoji: '⚪' },
    { symbol: 'BRENT', name: 'Brent Crude Oil', yahooSymbol: 'BZ=F', color: '#1a1a2e', emoji: '🛢️' },
    { symbol: 'SOYBEAN', name: 'Soybean', yahooSymbol: 'ZS=F', color: '#84cc16', emoji: '🫘' },
    { symbol: 'COCOA', name: 'Cocoa', yahooSymbol: 'CC=F', color: '#7c4a2d', emoji: '🍫' },
    { symbol: 'COTTON', name: 'Cotton', yahooSymbol: 'CT=F', color: '#f8fafc', emoji: '🌱' },
    // Aluminum has thin/low-liquidity data on Yahoo's free feed — best-effort, flagged as a
    // reliability risk (unlike the other commodities above, which have deep active markets).
    { symbol: 'ALUMINUM', name: 'Aluminum', yahooSymbol: 'ALI=F', color: '#cbd5e1', emoji: '🔩' },
    // No free/live data source exists for these (LME-only metals). Price is admin-set via
    // System Settings > manualCommodityPrices instead of fetched live — not real-time.
    { symbol: 'NICKEL', name: 'Nickel', yahooSymbol: '', color: '#a3a3a3', emoji: '⚙️', isManualPrice: true },
    { symbol: 'ZINC', name: 'Zinc', yahooSymbol: '', color: '#c0c0c0', emoji: '⚙️', isManualPrice: true },
    { symbol: 'LEAD', name: 'Lead', yahooSymbol: '', color: '#64748b', emoji: '⚙️', isManualPrice: true },
    { symbol: 'TIN', name: 'Tin', yahooSymbol: '', color: '#9ca3af', emoji: '⚙️', isManualPrice: true },
]

// Forex — CFD-style, priced via Yahoo Finance's standard 'XXXYYY=X' format.
// 24/5 market — closed roughly Friday 22:00 UTC to Sunday 22:00 UTC (see
// isMarketOpen below). No order book — broker-quoted price only.
export const FOREX_PAIRS = [
    { symbol: 'EURUSD', name: 'Euro / US Dollar', yahooSymbol: 'EURUSD=X', color: '#3b82f6', emoji: '💶' },
    { symbol: 'GBPUSD', name: 'British Pound / US Dollar', yahooSymbol: 'GBPUSD=X', color: '#6366f1', emoji: '💷' },
    { symbol: 'USDJPY', name: 'US Dollar / Japanese Yen', yahooSymbol: 'USDJPY=X', color: '#ef4444', emoji: '💴' },
    { symbol: 'USDCHF', name: 'US Dollar / Swiss Franc', yahooSymbol: 'USDCHF=X', color: '#dc2626', emoji: '💵' },
    { symbol: 'AUDUSD', name: 'Australian Dollar / US Dollar', yahooSymbol: 'AUDUSD=X', color: '#f59e0b', emoji: '💵' },
    { symbol: 'NZDUSD', name: 'New Zealand Dollar / US Dollar', yahooSymbol: 'NZDUSD=X', color: '#84cc16', emoji: '💵' },
    { symbol: 'USDCAD', name: 'US Dollar / Canadian Dollar', yahooSymbol: 'USDCAD=X', color: '#dc2626', emoji: '💵' },
    { symbol: 'EURGBP', name: 'Euro / British Pound', yahooSymbol: 'EURGBP=X', color: '#3b82f6', emoji: '💶' },
    { symbol: 'EURJPY', name: 'Euro / Japanese Yen', yahooSymbol: 'EURJPY=X', color: '#3b82f6', emoji: '💶' },
    { symbol: 'EURCHF', name: 'Euro / Swiss Franc', yahooSymbol: 'EURCHF=X', color: '#3b82f6', emoji: '💶' },
    { symbol: 'EURAUD', name: 'Euro / Australian Dollar', yahooSymbol: 'EURAUD=X', color: '#3b82f6', emoji: '💶' },
    { symbol: 'EURNZD', name: 'Euro / New Zealand Dollar', yahooSymbol: 'EURNZD=X', color: '#3b82f6', emoji: '💶' },
    { symbol: 'EURCAD', name: 'Euro / Canadian Dollar', yahooSymbol: 'EURCAD=X', color: '#3b82f6', emoji: '💶' },
    { symbol: 'GBPJPY', name: 'British Pound / Japanese Yen', yahooSymbol: 'GBPJPY=X', color: '#6366f1', emoji: '💷' },
    { symbol: 'GBPCHF', name: 'British Pound / Swiss Franc', yahooSymbol: 'GBPCHF=X', color: '#6366f1', emoji: '💷' },
    { symbol: 'GBPAUD', name: 'British Pound / Australian Dollar', yahooSymbol: 'GBPAUD=X', color: '#6366f1', emoji: '💷' },
    { symbol: 'GBPCAD', name: 'British Pound / Canadian Dollar', yahooSymbol: 'GBPCAD=X', color: '#6366f1', emoji: '💷' },
    { symbol: 'AUDJPY', name: 'Australian Dollar / Japanese Yen', yahooSymbol: 'AUDJPY=X', color: '#f59e0b', emoji: '💵' },
    { symbol: 'AUDNZD', name: 'Australian Dollar / New Zealand Dollar', yahooSymbol: 'AUDNZD=X', color: '#f59e0b', emoji: '💵' },
    { symbol: 'AUDCAD', name: 'Australian Dollar / Canadian Dollar', yahooSymbol: 'AUDCAD=X', color: '#f59e0b', emoji: '💵' },
    { symbol: 'AUDCHF', name: 'Australian Dollar / Swiss Franc', yahooSymbol: 'AUDCHF=X', color: '#f59e0b', emoji: '💵' },
    { symbol: 'NZDJPY', name: 'New Zealand Dollar / Japanese Yen', yahooSymbol: 'NZDJPY=X', color: '#84cc16', emoji: '💵' },
    { symbol: 'NZDCAD', name: 'New Zealand Dollar / Canadian Dollar', yahooSymbol: 'NZDCAD=X', color: '#84cc16', emoji: '💵' },
    { symbol: 'NZDCHF', name: 'New Zealand Dollar / Swiss Franc', yahooSymbol: 'NZDCHF=X', color: '#84cc16', emoji: '💵' },
    { symbol: 'CADJPY', name: 'Canadian Dollar / Japanese Yen', yahooSymbol: 'CADJPY=X', color: '#dc2626', emoji: '💵' },
    { symbol: 'CADCHF', name: 'Canadian Dollar / Swiss Franc', yahooSymbol: 'CADCHF=X', color: '#dc2626', emoji: '💵' },
    { symbol: 'CHFJPY', name: 'Swiss Franc / Japanese Yen', yahooSymbol: 'CHFJPY=X', color: '#dc2626', emoji: '💵' },
]

// Stock Indices — priced via Yahoo Finance's caret-prefixed index tickers.
// Follows each exchange's local trading hours (see isMarketOpen below).
// NOTE: CHINA50 has no clean free ticker for the FTSE China A50 specifically —
// mapped to the SSE Composite Index (000001.SS) as the closest available proxy
// on Yahoo's free feed. Flagged for Zaman/client to confirm this substitution
// is acceptable, or if the exact China A50 needs a paid vendor later.
export const INDEX_PAIRS = [
    { symbol: 'US30', name: 'Dow Jones 30', yahooSymbol: '^DJI', color: '#2563eb', emoji: '📈' },
    { symbol: 'US500', name: 'S&P 500', yahooSymbol: '^GSPC', color: '#2563eb', emoji: '📈' },
    { symbol: 'NAS100', name: 'Nasdaq 100', yahooSymbol: '^NDX', color: '#2563eb', emoji: '📈' },
    { symbol: 'UK100', name: 'FTSE 100', yahooSymbol: '^FTSE', color: '#dc2626', emoji: '📈' },
    { symbol: 'GER40', name: 'DAX 40', yahooSymbol: '^GDAXI', color: '#f59e0b', emoji: '📈' },
    { symbol: 'FRA40', name: 'CAC 40', yahooSymbol: '^FCHI', color: '#3b82f6', emoji: '📈' },
    { symbol: 'EU50', name: 'Euro Stoxx 50', yahooSymbol: '^STOXX50E', color: '#3b82f6', emoji: '📈' },
    { symbol: 'AUS200', name: 'ASX 200', yahooSymbol: '^AXJO', color: '#84cc16', emoji: '📈' },
    { symbol: 'HK50', name: 'Hang Seng', yahooSymbol: '^HSI', color: '#dc2626', emoji: '📈' },
    { symbol: 'JPN225', name: 'Nikkei 225', yahooSymbol: '^N225', color: '#ef4444', emoji: '📈' },
    // Best-effort proxy — see note above.
    { symbol: 'CHINA50', name: 'China A50 (SSE Composite proxy)', yahooSymbol: '000001.SS', color: '#dc2626', emoji: '📈' },
    { symbol: 'INDIA50', name: 'Nifty 50', yahooSymbol: '^NSEI', color: '#f59e0b', emoji: '📈' },
]

// Which markets are 24/5 (forex) or exchange-hours-restricted (indices) rather
// than 24/7 like crypto or "always tradable" like the stock/commodity CFDs
// already in this app. Approximate — ignores holidays and doesn't precisely
// track DST shifts (~1hr drift possible for European/UK indices depending on
// time of year). Flagged as a known v1 limitation, not exact exchange hours.
const INDEX_HOURS_UTC: Record<string, { openHour: number; closeHour: number }> = {
    US30: { openHour: 13.5, closeHour: 20 },
    US500: { openHour: 13.5, closeHour: 20 },
    NAS100: { openHour: 13.5, closeHour: 20 },
    UK100: { openHour: 8, closeHour: 16.5 },
    GER40: { openHour: 8, closeHour: 16.5 },
    FRA40: { openHour: 8, closeHour: 16.5 },
    EU50: { openHour: 8, closeHour: 16 },
    AUS200: { openHour: 0, closeHour: 6 },
    HK50: { openHour: 1.5, closeHour: 8 },
    JPN225: { openHour: 0, closeHour: 6 },
    CHINA50: { openHour: 1.5, closeHour: 7 },
    INDIA50: { openHour: 3.75, closeHour: 10 },
}

/**
 * Returns whether the given forex/index instrument's market is currently open.
 * Crypto and the existing stock/commodity CFDs are always considered "open"
 * (matches current app behavior — no hours gating existed for those before).
 */
export const isMarketOpen = (category: 'crypto' | 'stocks' | 'commodities' | 'forex' | 'indices', symbol: string): boolean => {
    const now = new Date()
    const day = now.getUTCDay() // 0 = Sunday, 6 = Saturday
    const hour = now.getUTCHours() + now.getUTCMinutes() / 60

    if (category === 'forex') {
        // Closed from Friday ~22:00 UTC through Sunday ~22:00 UTC
        if (day === 6) return false
        if (day === 0 && hour < 22) return false
        if (day === 5 && hour >= 22) return false
        return true
    }
    if (category === 'indices') {
        if (day === 0 || day === 6) return false
        const hours = INDEX_HOURS_UTC[symbol]
        if (!hours) return true
        return hour >= hours.openHour && hour < hours.closeHour
    }
    return true
}

// Baseline prices used for mock chart when Yahoo Finance is unavailable
const NON_CRYPTO_BASE: Record<string, number> = {
    AAPL: 227, MSFT: 405, NVDA: 875, GOOGL: 178, AMZN: 220, TSLA: 245, META: 560,
    NFLX: 910, JPM: 220, V: 295, WMT: 98, DIS: 112, AMD: 178, INTC: 20, COIN: 195,
    PYPL: 82, UBER: 75, ORCL: 165, CRM: 310, BABA: 82,
    GS: 490, MS: 95, BAC: 38, XOM: 112, CVX: 155, NKE: 92, KO: 62, PEP: 172,
    MCD: 295, SBUX: 90, JNJ: 150, PG: 165, BA: 175, TSM: 145, ASML: 790,
    SHOP: 65, ABNB: 145, PLTR: 22, SNOW: 150, SPOT: 295, SQ: 70, HOOD: 18,
    MSTR: 1600, ARM: 120, AVGO: 1400, QCOM: 160, MU: 85, ADBE: 460, NOW: 820, RBLX: 40,
    '2222.SR': 30, RTX: 90, 'BRK-B': 400, LLY: 780, UNH: 520, HD: 360, COST: 750,
    ABBV: 170, MRK: 125, TMO: 580, CSCO: 50, TMUS: 165, TXN: 175, NVO: 125,
    TM: 240, SAP: 190, SONY: 95, BHP: 60, LMT: 450, CAT: 330,
    'GC=F': 2050, 'SI=F': 23, 'CL=F': 78, 'NG=F': 2.8, 'ZW=F': 560,
    'ZC=F': 420, 'HG=F': 3.85, 'PL=F': 980, 'KC=F': 190, 'SB=F': 22,
    // New commodities (client list expansion)
    'PA=F': 1000, 'BZ=F': 82, 'ZS=F': 1050, 'CC=F': 8500, 'CT=F': 68, 'ALI=F': 2400,
    // Forex (approximate seed only — replaced immediately by live fetch)
    'EURUSD=X': 1.08, 'GBPUSD=X': 1.27, 'USDJPY=X': 155, 'USDCHF=X': 0.90,
    'AUDUSD=X': 0.66, 'NZDUSD=X': 0.61, 'USDCAD=X': 1.37, 'EURGBP=X': 0.85,
    'EURJPY=X': 167, 'EURCHF=X': 0.97, 'EURAUD=X': 1.63, 'EURNZD=X': 1.77,
    'EURCAD=X': 1.48, 'GBPJPY=X': 196, 'GBPCHF=X': 1.14, 'GBPAUD=X': 1.92,
    'GBPCAD=X': 1.74, 'AUDJPY=X': 102, 'AUDNZD=X': 1.08, 'AUDCAD=X': 0.90,
    'AUDCHF=X': 0.59, 'NZDJPY=X': 94, 'NZDCAD=X': 0.83, 'NZDCHF=X': 0.55,
    'CADJPY=X': 113, 'CADCHF=X': 0.66, 'CHFJPY=X': 172,
    // Indices (approximate seed only — replaced immediately by live fetch)
    '^DJI': 40000, '^GSPC': 5500, '^NDX': 19500, '^FTSE': 8200, '^GDAXI': 18500,
    '^FCHI': 7500, '^STOXX50E': 4900, '^AXJO': 7800, '^HSI': 18000, '^N225': 38000,
    '000001.SS': 3000, '^NSEI': 24000,
}

// Single source of truth for "which array backs this non-crypto category" —
// avoids repeating `cat === 'stocks' ? STOCK_PAIRS : COMMODITY_PAIRS`-style
// ternaries at every usage site now that there are 4 non-crypto categories.
type NonCryptoPairItem = { symbol: string; name: string; yahooSymbol: string; color?: string; emoji?: string; isManualPrice?: boolean }
const CATEGORY_PAIRS: Record<'stocks' | 'commodities' | 'forex' | 'indices', NonCryptoPairItem[]> = {
    stocks: STOCK_PAIRS,
    commodities: COMMODITY_PAIRS,
    forex: FOREX_PAIRS,
    indices: INDEX_PAIRS,
}
const CATEGORY_LABELS: Record<'stocks' | 'commodities' | 'forex' | 'indices', string> = {
    stocks: 'Stock',
    commodities: 'Commodity',
    forex: 'Forex',
    indices: 'Index',
}

// Safe logo component — uses React state for fallback, no DOM manipulation
const SYMBOL_COLORS: Record<string, string> = {
    A:'#e74c3c',B:'#3498db',C:'#2ecc71',D:'#9b59b6',E:'#f39c12',
    F:'#1abc9c',G:'#e67e22',H:'#34495e',I:'#e91e63',J:'#00bcd4',
    K:'#8bc34a',L:'#ff5722',M:'#607d8b',N:'#9c27b0',O:'#ff9800',
    P:'#03a9f4',Q:'#4caf50',R:'#f44336',S:'#2196f3',T:'#673ab7',
    U:'#009688',V:'#ffc107',W:'#795548',X:'#cddc39',Y:'#ff4081',Z:'#00bfa5',
}
// AssetIcon: tries src, then src2, then shows a coloured letter — all via React state, no DOM mutation
function AssetIcon({ src, src2, label, size = 18, radius = '50%', bgOverride }: {
    src?: string; src2?: string; label: string; size?: number; radius?: string | number; bgOverride?: string
}) {
    const [attempt, setAttempt] = useState<0 | 1 | 2>(0) // 0=try src, 1=try src2, 2=letter
    const letter = (label || '?').charAt(0).toUpperCase()
    const bg = bgOverride || SYMBOL_COLORS[letter] || '#4a4a6a'
    const activeSrc = attempt === 0 ? src : attempt === 1 ? src2 : undefined
    const handleError = () => {
        if (attempt === 0 && src2) setAttempt(1)
        else setAttempt(2)
    }
    if (!activeSrc) {
        return (
            <span style={{ width: size, height: size, borderRadius: radius, background: bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.48, fontWeight: 800, color: 'var(--ui-text-primary)', flexShrink: 0, letterSpacing: -0.5 }}>
                {letter}
            </span>
        )
    }
    return (
        <span style={{ width: size, height: size, borderRadius: radius, background: 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
            <img src={activeSrc} alt={label} style={{ width: size, height: size, objectFit: 'contain' }} onError={handleError} />
        </span>
    )
}

export default function Trade() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { toast } = useToast()

    const [orderType, setOrderType] = useState<'limit' | 'market' | 'stop'>('limit')
    const [chartTab, setChartTab] = useState<'chart' | 'info'>('chart')
    const [obTab, setObTab] = useState<'orderbooks' | 'trades'>('orderbooks')
    const [tradeType, setTradeType] = useState<'spot' | 'grid'>('spot')
    const [pairsTab, setPairsTab] = useState<'usdt' | 'grid'>('usdt')
    const [mobileObTab, setMobileObTab] = useState<'orderbook' | 'trades'>('trades')
    const [showOptionsOverlay, setShowOptionsOverlay] = useState(false)
    const [showLineOverlay, setShowLineOverlay] = useState(false)
    const [lineType, setLineType] = useState('Candles')
    const [optionType, setOptionType] = useState('Quick order')
    const [showSettingsSidebar, setShowSettingsSidebar] = useState(false)
    const [showPairSelector, setShowPairSelector] = useState(false)
    const [showMobilePairDropdown, setShowMobilePairDropdown] = useState(false)
    const [pairSearch, setPairSearch] = useState('')
    const [selectedTimeframe, setSelectedTimeframe] = useState('1D')
    const [ordersTab, setOrdersTab] = useState<'open' | 'history'>('open')

    const [livePrices, setLivePrices] = useState<Record<string, number>>(() => {
        // Restore last known prices from localStorage so buttons aren't disabled on re-visit
        try {
            const cached = localStorage.getItem('dw_live_prices_cache')
            return cached ? JSON.parse(cached) : {}
        } catch { return {} }
    })
    const [marketData, setMarketData] = useState<Record<string, any>>(() => {
        try {
            const cached = localStorage.getItem('dw_market_data_cache')
            return cached ? JSON.parse(cached) : {}
        } catch { return {} }
    })
    const [userBalance, setUserBalance] = useState<number>(() => getCachedBalance())
    const [balanceLoading, setBalanceLoading] = useState(true)
    const [selectedPair, setSelectedPair] = useState(() => {
        const pairParam = searchParams.get('pair')
        if (pairParam && PAIR_SYMBOLS.includes(pairParam)) return pairParam
        return localStorage.getItem('dw_spot_pair') || 'BTC/USDT'
    })
    const [tradeAmount, setTradeAmount] = useState('')
    const [tradePrice, setTradePrice] = useState('')
    const [tradeTotal, setTradeTotal] = useState('')
    const [submittingSide, setSubmittingSide] = useState<'buy' | 'sell' | null>(null)
    const [openTradesList, setOpenTradesList] = useState<any[]>([])
    const [ohlcData, setOhlcData] = useState<{ open: number; high: number; low: number; close: number; change: number }>({ open: 0, high: 0, low: 0, close: 0, change: 0 })
    const [askOrders, setAskOrders] = useState<{ price: number; amount: number; total: number }[]>([])
    const [bidOrders, setBidOrders] = useState<{ price: number; amount: number; total: number }[]>([])
    const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 768)

    // ── Market category tabs: Crypto / Stocks / Commodities / Forex / Indices ──
    const [marketCategory, setMarketCategory] = useState<'crypto' | 'stocks' | 'commodities' | 'forex' | 'indices'>(
        () => (localStorage.getItem('dw_spot_category') as 'crypto' | 'stocks' | 'commodities' | 'forex' | 'indices') || 'crypto'
    )
    const [activeYahooSymbol, setActiveYahooSymbol] = useState(
        () => localStorage.getItem('dw_spot_yahoo_symbol') || ''
    )
    const [activeNonCryptoName, setActiveNonCryptoName] = useState(
        () => localStorage.getItem('dw_spot_noncrypto_name') || ''
    )
    const [activeNonCryptoSymbol, setActiveNonCryptoSymbol] = useState(
        () => localStorage.getItem('dw_spot_noncrypto_symbol') || ''
    )
    const [nonCryptoPrices, setNonCryptoPrices] = useState<Record<string, { price: number; change: number; high: number; low: number; volume: number }>>(() => {
        // Seed immediately from static baseline so currentPrice is never 0 on first render
        const seed: Record<string, { price: number; change: number; high: number; low: number; volume: number }> = {}
        ;[...STOCK_PAIRS, ...COMMODITY_PAIRS, ...FOREX_PAIRS, ...INDEX_PAIRS].forEach(item => {
            const base = NON_CRYPTO_BASE[item.yahooSymbol] || 100
            seed[item.yahooSymbol] = { price: base, change: 0, high: base, low: base, volume: 0 }
        })
        return seed
    })
    const [ticker24h, setTicker24h] = useState<{ high: number; low: number; volume: number; quoteVolume: number; change: number } | null>(null)
    const [fundingRate, setFundingRate] = useState<number>(-0.0070)

    const activePriceData = marketCategory !== 'crypto' && activeYahooSymbol ? (nonCryptoPrices[activeYahooSymbol] || null) : null
    const currentPrice = activePriceData
        ? activePriceData.price
        : (livePrices[toBinanceSymbol(selectedPair)] || marketData[selectedPair]?.price || 0)
    const selectedCoin = marketCategory === 'crypto' ? selectedPair.split('/')[0] : activeNonCryptoName
    // Only forex/indices are hours-gated — crypto is 24/7, stocks/commodities in this
    // app have always been treated as always-tradable (no change to that behavior here).
    const marketClosed = (marketCategory === 'forex' || marketCategory === 'indices') && !!activeNonCryptoSymbol && !isMarketOpen(marketCategory, activeNonCryptoSymbol)
    const currentPriceRef = useRef(currentPrice)
    const marketDataRef = useRef<Record<string, any>>(marketData)
    useEffect(() => { currentPriceRef.current = currentPrice }, [currentPrice])
    useEffect(() => { marketDataRef.current = marketData }, [marketData])

    // Persist live prices to localStorage so next visit doesn't start at price 0
    useEffect(() => {
        if (Object.keys(livePrices).length > 0) {
            try { localStorage.setItem('dw_live_prices_cache', JSON.stringify(livePrices)) } catch { /* quota */ }
        }
    }, [livePrices])

    useEffect(() => {
        if (Object.keys(marketData).length > 0) {
            try { localStorage.setItem('dw_market_data_cache', JSON.stringify(marketData)) } catch { /* quota */ }
        }
    }, [marketData])

    const handleNumericInput = (value: string, setter: (v: string) => void) => {
        const normalized = value.replace(',', '.')
        const cleaned = normalized.replace(/[^0-9.]/g, '')
        const parts = cleaned.split('.')
        if (parts.length > 2) return
        setter(cleaned)
    }

    // Track which field the user last typed in so the two-way sync doesn't loop
    const lastEditedField = useRef<'amount' | 'total'>('amount')

    useEffect(() => {
        if (lastEditedField.current !== 'amount') return
        const amt = parseFloat(tradeAmount) || 0
        const price = orderType === 'market' ? currentPrice : (parseFloat(tradePrice) || 0)
        if (amt > 0 && price > 0) setTradeTotal((amt * price).toFixed(2))
        else setTradeTotal('')
    }, [tradeAmount, tradePrice, currentPrice, orderType])

    const handleTotalChange = (val: string) => {
        const cleaned = val.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
        lastEditedField.current = 'total'
        setTradeTotal(cleaned)
        const total = parseFloat(cleaned) || 0
        const price = orderType === 'market' ? currentPrice : (parseFloat(tradePrice) || 0)
        if (total > 0 && price > 0) setTradeAmount((total / price).toFixed(6))
        else setTradeAmount('')
    }

    const handleAmountChange = (val: string) => {
        lastEditedField.current = 'amount'
        handleNumericInput(val, setTradeAmount)
    }

    useEffect(() => {
        if (currentPrice > 0 && orderType !== 'market') {
            const formatted = formatLivePrice(currentPrice)
            setTradePrice(formatted)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPair, currentPrice > 0 ? Math.round(currentPrice * 100000) : 0])

    useEffect(() => {
        const fetchUserBalance = async () => {
            try {
                const balRes = await getBalance().catch(() => ({ balance: 0 }))
                const fetchedBal = balRes.balance ?? 0
                setUserBalance(fetchedBal)
                // Always trust the backend value directly — including a legitimate 0 —
                // instead of only caching when > 0, which let stale cache linger.
                setCachedBalance(fetchedBal)
            } catch (e) { console.warn('Balance fetch failed:', e) } finally {
                setBalanceLoading(false)
            }
        }
        
        fetchUserBalance()
        
        const interval = setInterval(fetchUserBalance, 10000)
        return () => clearInterval(interval)
    }, [])

    // Real-time market prices via Binance WebSocket, with backend polling fallback.
    useEffect(() => {
        let wsList: WebSocket[] = [];
        let retryTimeouts: any[] = [];
        let fallbackPollInterval: any;
        let connectedCount = 0;

        const mergeMarketSnapshot = (snapshot: Record<string, any>) => {
            setLivePrices(prev => {
                const next = { ...prev };
                let changed = false;

                for (const pair of PAIR_SYMBOLS) {
                    const data = snapshot[pair];
                    const price = Number(data?.price);
                    if (!(price > 0)) continue;

                    const binanceKey = toBinanceSymbol(pair);
                    if (next[binanceKey] !== price) {
                        next[binanceKey] = price;
                        changed = true;
                    }
                    if (next[pair] !== price) {
                        next[pair] = price;
                        changed = true;
                    }
                }

                return changed ? next : prev;
            });

            setMarketData(prev => {
                const next = { ...prev };
                let changed = false;

                for (const pair of PAIR_SYMBOLS) {
                    const data = snapshot[pair];
                    const price = Number(data?.price);
                    if (!(price > 0)) continue;

                    const existing = next[pair] || {};
                    const merged = {
                        ...existing,
                        price,
                        change24h: typeof data?.change24h === 'number' ? data.change24h : (existing.change24h || 0),
                        volume24h: typeof data?.volume24h === 'number' ? data.volume24h : (existing.volume24h || 0),
                        marketCap: typeof data?.marketCap === 'number' ? data.marketCap : (existing.marketCap || 0),
                    };

                    if (
                        existing.price !== merged.price ||
                        existing.change24h !== merged.change24h ||
                        existing.volume24h !== merged.volume24h ||
                        existing.marketCap !== merged.marketCap
                    ) {
                        next[pair] = merged;
                        changed = true;
                    }
                }

                return changed ? next : prev;
            });
        };

        const fetchFallbackMarketData = async () => {
            try {
                const snapshot = await getDetailedMarketData(PAIR_SYMBOLS);
                mergeMarketSnapshot(snapshot);
            } catch {
                // Keep current UI values on transient network failures.
            }
        };

        const connectWS = () => {
            // Use targeted miniTicker streams per pair instead of !ticker@arr (all-pairs stream).
            // !ticker@arr sends 200+ symbols every second — Binance blocks it more aggressively and
            // it floods the browser. Per-pair miniTicker streams are lightweight and reliable.
            const cryptoPairs = PAIR_SYMBOLS.filter(p => !p.includes('STOCK') && !p.includes('COMM'));

            // Binance caps combined streams at 200 per connection. With 300+ pairs now
            // supported we must split across multiple WebSocket connections — each batch
            // reconnects independently on close/error so one dropping doesn't kill the rest.
            const BATCH_SIZE = 190;
            const batches: string[][] = [];
            for (let i = 0; i < cryptoPairs.length; i += BATCH_SIZE) {
                batches.push(cryptoPairs.slice(i, i + BATCH_SIZE));
            }

            wsList = [];
            retryTimeouts = [];
            connectedCount = 0;

            batches.forEach((batch, batchIndex) => {
                const connectBatch = () => {
                    try {
                        const streams = batch
                            .map(p => `${p.replace('/', '').toLowerCase()}@miniTicker`)
                            .join('/');
                        // Port 443 works through stricter firewalls than 9443
                        const socket = new WebSocket(`wss://stream.binance.com:443/stream?streams=${streams}`);
                        wsList[batchIndex] = socket;
                        socket.onopen = () => { connectedCount++; };
                        socket.onmessage = (event) => {
                            try {
                                const msg = JSON.parse(event.data);
                                // Multi-stream wrapper: { stream: "xrpusdt@miniTicker", data: {...} }
                                const tick = msg.data || msg;
                                if (tick?.s && tick?.c) {
                                    const val = parseFloat(tick.c);
                                    if (!(val > 0)) return;
                                    const cleanPair = tick.s.replace('USDT', '/USDT');
                                    setLivePrices(prev => {
                                        if (prev[tick.s] === val) return prev;
                                        return { ...prev, [tick.s]: val, [cleanPair]: val };
                                    });
                                    setMarketData(prev => {
                                        const existing = prev[cleanPair] || {};
                                        // Binance's miniTicker stream has no 'P' (percent change) field —
                                        // that only exists on the full @ticker stream. miniTicker does
                                        // give us open ('o') and close ('c'), so compute the % change
                                        // ourselves instead of reading a field that's always undefined
                                        // (which was silently freezing every pair's 24h change at 0%).
                                        const openPrice = parseFloat(tick.o);
                                        const computedChange = openPrice > 0 ? ((val - openPrice) / openPrice) * 100 : (existing.change24h || 0);
                                        return {
                                            ...prev,
                                            [cleanPair]: {
                                                ...existing,
                                                price: val,
                                                change24h: computedChange,
                                                high24h: parseFloat(tick.h) || existing.high24h || 0,
                                                low24h: parseFloat(tick.l) || existing.low24h || 0,
                                                high: parseFloat(tick.h) || existing.high || 0,
                                                low: parseFloat(tick.l) || existing.low || 0,
                                                volume24h: parseFloat(tick.v) || existing.volume24h || 0,
                                                quoteVolume24h: parseFloat(tick.q) || existing.quoteVolume24h || 0,
                                            }
                                        };
                                    });
                                }
                            } catch { /* ignore parse errors */ }
                        };
                        socket.onerror = () => { connectedCount = Math.max(0, connectedCount - 1); socket.close(); };
                        socket.onclose = () => {
                            connectedCount = Math.max(0, connectedCount - 1);
                            retryTimeouts[batchIndex] = setTimeout(connectBatch, 5000);
                        };
                    } catch (err) {
                        retryTimeouts[batchIndex] = setTimeout(connectBatch, 5000);
                    }
                };
                connectBatch();
            });
        };

        fetchFallbackMarketData();
        fallbackPollInterval = setInterval(() => {
            if (connectedCount === 0) fetchFallbackMarketData();
        }, 12000);

        connectWS();
        return () => {
            retryTimeouts.forEach(t => clearTimeout(t));
            clearInterval(fallbackPollInterval);
            wsList.forEach(socket => {
                if (socket) {
                    socket.onclose = null;
                    socket.onerror = null;
                    socket.close();
                }
            });
        };
    }, []);

    useEffect(() => {
        const fetchTrades = async () => {
            try {
                const trades = await getMyOpenTrades()
                // Sync localStorage holdings with backend open spot trades so that
                // admin-opened trades appear in the user's portfolio and sell validation works.
                syncSpotHoldingsFromBackend(trades || [])
                setOpenTradesList((trades || []).filter((t: any) => !t.type || t.type === 'spot'))
            } catch { /* ignore */ }
        }
        fetchTrades()
        const interval = setInterval(fetchTrades, 15000)
        return () => clearInterval(interval)
    }, [])

    // ── Binance 24h ticker — real high/low/volume/change ────────────────────
    useEffect(() => {
        if (marketCategory !== 'crypto') return
        const symbol = toBinanceSymbol(selectedPair)
        const applyFallback = (price: number) => {
            const selectedData = marketDataRef.current[selectedPair] || {}
            const volume = typeof selectedData.volume24h === 'number' ? selectedData.volume24h : 0
            const quoteVolume = typeof selectedData.quoteVolume24h === 'number' ? selectedData.quoteVolume24h : volume * price
            const change = typeof selectedData.change24h === 'number' ? selectedData.change24h : 0
            const high = (typeof selectedData.high24h === 'number' ? selectedData.high24h : selectedData.high) || (price > 0 ? price * 1.018 : 0)
            const low = (typeof selectedData.low24h === 'number' ? selectedData.low24h : selectedData.low) || (price > 0 ? price * 0.983 : 0)

            setTicker24h({ high, low, volume, quoteVolume, change })
            setFundingRate(0)
        }
        fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${symbol}`)
            .then(r => r.json())
            .then((d: any) => {
                if (d.highPrice) {
                    setTicker24h({
                        high: parseFloat(d.highPrice),
                        low: parseFloat(d.lowPrice),
                        volume: parseFloat(d.volume),
                        quoteVolume: parseFloat(d.quoteVolume),
                        change: parseFloat(d.priceChangePercent)
                    })
                    setFundingRate(+(Math.random() * 0.08 - 0.04).toFixed(4))
                } else { applyFallback(currentPriceRef.current || 65000) }
            })
            .catch(() => applyFallback(currentPriceRef.current || 65000))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPair, marketCategory])

    useEffect(() => {
        if (currentPrice <= 0) return
        const p = currentPrice
        const asks = Array.from({ length: 18 }, (_, i) => {
            const offset = (i + 1) * (p * 0.0001 * (1 + Math.random()))
            const price = parseFloat((p + offset).toFixed(2))
            const amount = parseFloat((Math.random() * 0.5 + 0.001).toFixed(5))
            return { price, amount, total: parseFloat((price * amount).toFixed(5)) }
        }).sort((a, b) => a.price - b.price)
        const bids = Array.from({ length: 18 }, (_, i) => {
            const offset = (i + 1) * (p * 0.0001 * (1 + Math.random()))
            const price = parseFloat((p - offset).toFixed(2))
            const amount = parseFloat((Math.random() * 0.5 + 0.001).toFixed(5))
            return { price, amount, total: parseFloat((price * amount).toFixed(5)) }
        }).sort((a, b) => b.price - a.price)
        setAskOrders(asks)
        setBidOrders(bids)
    }, [currentPrice, selectedPair])

    const handleTrade = async (side: 'buy' | 'sell') => {
        const coinAmt = Number(tradeAmount)
        if (!tradeAmount || isNaN(coinAmt) || coinAmt <= 0) { toast('Please enter a valid amount', 'warning'); return }
        if (currentPrice <= 0) { toast('Loading price data, please wait a moment...', 'info'); return }
        if (marketClosed) { toast('Market is closed for this instrument. Trading resumes when it reopens.', 'warning'); return }

        const isLimitOrder = orderType === 'limit'
        const limitPriceVal = parseFloat(tradePrice) || 0

        // For limit orders, validate limit price is set and makes sense
        if (isLimitOrder) {
            if (!limitPriceVal || limitPriceVal <= 0) { toast('Please enter a valid limit price', 'warning'); return }
            // Warn if limit price is far from current price (likely typo like $20 for BTC)
            const priceDiff = Math.abs(limitPriceVal - currentPrice) / currentPrice
            if (priceDiff > 0.5) {
                toast(`Limit price $${limitPriceVal.toLocaleString()} is more than 50% away from current price $${currentPrice.toLocaleString()}. Check your entry.`, 'warning')
                return
            }
        }

        // For cost calculation: use limit price for limits, current price for market
        const priceForCost = isLimitOrder ? limitPriceVal : currentPrice
        const usdtCost = parseFloat((coinAmt * priceForCost).toFixed(2))
        const holdingKey = marketCategory !== 'crypto' && activeNonCryptoSymbol ? activeNonCryptoSymbol : selectedCoin

        if (side === 'buy') {
            if (usdtCost > userBalance) { toast('Insufficient balance', 'warning'); return }
        } else {
            const coinHolding = getSpotHolding(holdingKey)
            if (coinAmt > coinHolding + 1e-8) { toast(`Insufficient ${selectedCoin} balance`, 'warning'); return }
        }

        setSubmittingSide(side)
        try {
            const tradeAsset = holdingKey
            await openTrade({
                asset: tradeAsset,
                type: 'spot',
                side,
                amount: parseFloat(coinAmt.toFixed(8)),
                // Market order: send live price so backend uses it for margin/cost
                // Limit order: send limitPrice — backend creates as 'pending', fills when price is hit
                price: isLimitOrder ? undefined : currentPrice,
                entryPrice: isLimitOrder ? undefined : currentPrice,
                orderType: isLimitOrder ? 'limit' : 'market',
                limitPrice: isLimitOrder ? limitPriceVal : undefined,
            })

            if (isLimitOrder) {
                toast(`Limit ${side} order placed at $${limitPriceVal.toLocaleString()}. Will fill when price is reached.`, 'success')
            } else {
                const usdtDelta = side === 'buy' ? -usdtCost : usdtCost
                const newBal = parseFloat((userBalance + usdtDelta).toFixed(2))
                setUserBalance(newBal)
                setCachedBalance(newBal)
                if (side === 'buy') addSpotHolding(holdingKey, coinAmt, usdtCost)
                else deductSpotHolding(holdingKey, coinAmt)
                toast(`${side === 'buy' ? 'Buy' : 'Sell'} order placed!`, 'success')
            }
            setTradeAmount(''); setTradeTotal('')
            try {
                const [balRes, trades] = await Promise.all([getBalance(), getMyOpenTrades()])
                setUserBalance(balRes.balance ?? 0)
                setOpenTradesList((trades || []).filter((t: any) => !t.type || t.type === 'spot'))
            } catch { /* keep local state */ }
        } catch (e: any) { toast(e.message || 'Trade failed', 'error') }
        finally { setSubmittingSide(null) }
    }

    const handleCloseTrade = async (tradeId: string) => {
        try {
            const trade = openTradesList.find(t => t._id === tradeId)
            if (tradeId.startsWith('local_')) {
                // For local (offline) spot trades: return what the coins are worth at current price.
                // Do NOT add trade.pnl separately — it would double-count the position value.
                const tradeValue = trade ? parseFloat((trade.amount * (currentPrice || trade.entryPrice)).toFixed(2)) : 0
                setOpenTradesList(prev => prev.filter(t => t._id !== tradeId))
                setUserBalance(prev => parseFloat((prev + tradeValue).toFixed(2)))
            } else {
                await closeTrade(tradeId)
                // Backend handles all balance updates for spot trades on close;
                // do NOT apply applyLocalBalanceChange here to avoid double-counting.
                const [balRes, trades] = await Promise.all([getBalance(), getMyOpenTrades()])
                setUserBalance(balRes.balance)
                setOpenTradesList((trades || []).filter((t: any) => !t.type || t.type === 'spot'))
            }
            toast('Trade closed successfully', 'success')
        } catch (e: any) { toast(e.message || 'Failed to close trade', 'error') }
    }

    const handleSelectPair = (pair: string) => {
        localStorage.setItem('dw_spot_pair', pair); setSelectedPair(pair); setShowPairSelector(false); setPairSearch(''); setTradeAmount(''); setTradeTotal('')
    }

    const filteredPairs = PAIR_SYMBOLS.filter(p =>
        p.toLowerCase().includes(pairSearch.toLowerCase()) ||
        (COIN_NAMES[p.split('/')[0]] || '').toLowerCase().includes(pairSearch.toLowerCase())
    )

    const tradingPairs = PAIR_SYMBOLS.map(pair => {
        const coin = pair.split('/')[0]
        const wsPrice = livePrices[toBinanceSymbol(pair)] || 0
        const data = marketData[pair] || {}
        const price = wsPrice > 0 ? wsPrice : (data.price || 0)
        const change = data.change24h || 0
        return {
            symbol: coin, name: COIN_NAMES[coin] || coin, pair: 'USDT', fullPair: pair, price,
            priceStr: price ? `$${formatLivePrice(price)}` : 'Loading',
            change: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
            positive: change >= 0,
        }
    })

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth <= 768)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        if (marketCategory === 'crypto') return
          const items = CATEGORY_PAIRS[marketCategory]
          let active = true

          const fetchPrices = async () => {
              const symbols = items.map(i => `${i.symbol}/USDT`)
              try {
                  const res = await getMarketPrices(symbols)
                  if (!active) return
                  
                  setNonCryptoPrices(prev => {
                      const next = { ...prev }
                      let changed = false
                      for (const item of items) {
                          const px = res[`${item.symbol}/USDT`] || res[`${item.symbol}USDT`] || res[item.symbol]
                          if (px > 0) {
                              const oldPrice = prev[item.yahooSymbol]?.price || NON_CRYPTO_BASE[item.yahooSymbol] || 100
                              const change = oldPrice ? ((px - oldPrice) / oldPrice) * 100 : 0
                              next[item.yahooSymbol] = {
                                  price: px,
                                  change: change !== 0 ? change : (prev[item.yahooSymbol]?.change || 0),
                                  high: Math.max(px, prev[item.yahooSymbol]?.high || px),
                                  low: prev[item.yahooSymbol]?.low ? Math.min(px, prev[item.yahooSymbol].low) : px,
                                  volume: prev[item.yahooSymbol]?.volume || Math.floor(Math.random() * 5e7)
                              }
                              changed = true
                          } else if (!prev[item.yahooSymbol]) {
                              const seed = NON_CRYPTO_BASE[item.yahooSymbol] || 100
                              next[item.yahooSymbol] = { price: seed, change: 0, high: seed, low: seed, volume: 0 }
                              changed = true
                          }
                      }
                      return changed ? next : prev
                  })
              } catch {
                  if (!active) return
                  setNonCryptoPrices(prev => {
                       const next = { ...prev }
                       let changed = false
                       for (const item of items) {
                           if (!prev[item.yahooSymbol]) {
                               const seed = NON_CRYPTO_BASE[item.yahooSymbol] || 100
                               next[item.yahooSymbol] = { price: seed, change: 0, high: seed, low: seed, volume: 0 }
                               changed = true
                           }
                       }
                       return changed ? next : prev
                  })
              }
          }
          
          fetchPrices()
          const interval = setInterval(fetchPrices, 4000)
          return () => { active = false; clearInterval(interval) }
    }, [marketCategory])

    const formatPrice = (price: number) => {
        if (price === 0) return '0.00'
        if (price >= 1000) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        if (price >= 1) return price.toFixed(4)
        return price.toFixed(6)
    }

    /**
     * Compute real-time P&L for an open trade using live WebSocket price data.
     * This updates every ~1 second (Binance WS tick) instead of every 15 seconds
     * (backend poll), giving Binance-style smooth P&L movement.
     *
     * Falls back to backend's unrealizedPnL if no live price is available
     * (e.g. stocks/commodities before first Yahoo Finance fetch).
     */
    const computeLivePnL = (trade: any): number => {
        if (trade.status !== 'open') return trade.pnl ?? 0
        const asset = (trade.asset || '').split('/')[0].toUpperCase()
        const binanceKey = `${asset}USDT`
        // Try WS crypto price first, then non-crypto price map, then backend value
        const livePrice =
            livePrices[binanceKey] ||
            (trade.market === 'stock' || trade.market === 'commodities'
                ? nonCryptoPrices[trade.yahooSymbol || '']?.price || 0
                : 0)
        if (!livePrice) return trade.unrealizedPnL ?? trade.pnl ?? 0
        const isLong = trade.side === 'buy' || trade.side === 'long'
        const raw = isLong
            ? (livePrice - trade.entryPrice) * trade.amount
            : (trade.entryPrice - livePrice) * trade.amount
        return Math.max(raw, -(trade.marginUsed || Infinity))
    }

    const pairChangeData = marketData[selectedPair] || {}
    const _p = currentPrice || currentPriceRef.current || 0
    const change24h = activePriceData ? activePriceData.change : (ticker24h ? ticker24h.change : (pairChangeData.change24h || 0))
    const volume24h = activePriceData
        ? activePriceData.volume
        : (ticker24h ? ticker24h.volume : (pairChangeData.volume24h || pairChangeData.volume || (_p > 0 ? +(_p * 0.18 + Math.random() * _p * 0.05).toFixed(0) : 0)))
    const high24h = activePriceData
        ? activePriceData.high
        : (ticker24h ? ticker24h.high : (pairChangeData.high24h || pairChangeData.high || ohlcData.high || (_p > 0 ? +(_p * 1.018).toFixed(2) : 0)))
    const low24h = activePriceData
        ? activePriceData.low
        : (ticker24h ? ticker24h.low : (pairChangeData.low24h || pairChangeData.low || ohlcData.low || (_p > 0 ? +(_p * 0.983).toFixed(2) : 0)))
    const quoteVol24h = ticker24h ? ticker24h.quoteVolume : (pairChangeData.quoteVolume24h || (volume24h * _p))
    const timeframes = ['1s', '15m', '1H', '4H', '1D', '1W']

    return (
        <Layout activePage="trade" hideFooter={false} hideFooterMobile={true} hideMobileNav={false} hideMobileHeader={true}>
            {showPairSelector && (
                <div className="tm-overlay-v3" onClick={() => setShowPairSelector(false)} style={{ zIndex: 1000 }}>
                    <div className="tm-overlay-content-v3" onClick={e => e.stopPropagation()} style={{ maxHeight: '70vh', overflow: 'auto' }}>
                        <div className="tm-overlay-header-v3">
                            <span className="tm-overlay-title">Select Pair</span>
                            <button className="tm-close-overlay" onClick={() => setShowPairSelector(false)}>{"\u2715"}</button>
                        </div>
                        <div style={{ padding: '8px 16px' }}>
                            <input type="text" placeholder="Search..." value={pairSearch} onChange={e => setPairSearch(e.target.value)}
                                style={{ width: '100%', padding: '8px 12px', background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 8, color: 'var(--ui-text-primary)', fontSize: 14, outline: 'none' }} />
                        </div>
                        <div className="tm-overlay-list-v3">
                            {filteredPairs.map(pair => {
                                const coin = pair.split('/')[0], md = marketData[pair] || {}
                                const price = livePrices[toBinanceSymbol(pair)] || md.price || 0
                                return (
                                    <div key={pair} className="tm-overlay-item-v3" onClick={() => handleSelectPair(pair)}
                                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                                        <div>
                                            <span style={{ fontWeight: 600 }}>{coin}</span>
                                            <span style={{ color: 'var(--ui-text-muted)', marginLeft: 4, fontSize: 12 }}>/USDT</span>
                                            <div style={{ fontSize: 11, color: 'var(--ui-text-muted)' }}>{COIN_NAMES[coin] || coin}</div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ color: 'var(--ui-text-primary)', fontSize: 13 }}>{formatPrice(price)}</div>
                                            <div style={{ fontSize: 11, color: (md.change24h || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                                                {md.change24h ? `${md.change24h >= 0 ? '+' : ''}${md.change24h.toFixed(2)}%` : 'Live'}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div className="mobile-only">
                <div className="trade-mobile-layout-v3">
                    <div className="tm-header-v3" style={{ position: 'relative' }}>
                        <div className="tm-header-left">
                            <ArrowLeft size={22} className="tm-back-icon" onClick={() => navigate('/dashboard/home')} />
                            <div className="tm-pair-box" onClick={() => setShowMobilePairDropdown(v => !v)} style={{ cursor: 'pointer' }}>
                                <span className="tm-pair-name">{marketCategory === 'crypto' ? selectedPair : activeNonCryptoName || selectedPair}</span>
                                <ChevronDown size={16} className="tm-pair-arrow" style={{ transition: 'transform 0.2s', transform: showMobilePairDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                            </div>
                        </div>
                        <div className="tm-header-right">
                            <MoreHorizontal size={22} className="tm-more-icon" onClick={() => setShowSettingsSidebar(true)} />
                        </div>
                        {showMobilePairDropdown && (
                            <div className="tm-pair-dropdown">
                                {/* Search bar */}
                                <div className="tm-pair-dropdown-search">
                                    <Search size={14} className="tm-pair-dropdown-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search for currency pairs"
                                        className="tm-pair-dropdown-input"
                                        value={pairSearch}
                                        onChange={e => setPairSearch(e.target.value)}
                                        autoFocus
                                        onClick={e => e.stopPropagation()}
                                    />
                                </div>
                                {/* Category tabs: Crypto / Stocks / Commodities / Forex / Indices */}
                                <div className="tm-pair-dropdown-cats" onClick={e => e.stopPropagation()}>
                                    {(['crypto', 'stocks', 'commodities', 'forex', 'indices'] as const).map(cat => (
                                        <button
                                            key={cat}
                                            className={`tm-pair-dropdown-cat-btn ${marketCategory === cat ? 'active' : ''}`}
                                            onClick={() => {
                                                setPairSearch('')
                                                localStorage.setItem('dw_spot_category', cat)
                                                setMarketCategory(cat)
                                                if (cat !== 'crypto') {
                                                    const first = CATEGORY_PAIRS[cat][0]
                                                    localStorage.setItem('dw_spot_yahoo_symbol', first.yahooSymbol)
                                                    localStorage.setItem('dw_spot_noncrypto_name', first.name)
                                                    localStorage.setItem('dw_spot_noncrypto_symbol', first.symbol)
                                                    setActiveYahooSymbol(first.yahooSymbol)
                                                    setActiveNonCryptoName(first.name)
                                                    setActiveNonCryptoSymbol(first.symbol)
                                                }
                                            }}
                                        >
                                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                {/* Column headers */}
                                <div className="tm-pair-dropdown-header">
                                    <span>Name</span>
                                    <span>Price</span>
                                    <span style={{ textAlign: 'right' }}>24h Change</span>
                                </div>
                                {/* Lists */}
                                <div className="tm-pair-dropdown-list">
                                    {/* ── Crypto ── */}
                                    {marketCategory === 'crypto' && tradingPairs
                                        .filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase()))
                                        .map((pair, i) => (
                                            <div
                                                key={i}
                                                className={`tm-pair-dropdown-item ${pair.fullPair === selectedPair ? 'active' : ''}`}
                                                onClick={() => { handleSelectPair(pair.fullPair); setShowMobilePairDropdown(false); setPairSearch('') }}
                                            >
                                                <div className="tm-pair-dropdown-name">
                                                    <AssetIcon src={coinLogo(pair.symbol)} src2={coinLogoFallback(pair.symbol)} label={pair.symbol} size={18} radius="50%" />
                                                    <div style={{ marginLeft: 6 }}>
                                                        <span className="tm-pair-dropdown-symbol">{pair.symbol}</span>
                                                        <span className="tm-pair-dropdown-suffix">/USDT</span>
                                                        <div style={{ fontSize: 10, color: 'var(--ui-text-muted)' }}>{pair.price > 0 ? `$${pair.price.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}` : ''}</div>
                                                    </div>
                                                </div>
                                                <span className="tm-pair-dropdown-price">{pair.priceStr}</span>
                                                <span className={`tm-pair-dropdown-change ${pair.positive ? 'positive' : 'negative'}`}>{pair.change}</span>
                                            </div>
                                        ))
                                    }
                                    {/* ── Stocks ── */}
                                    {marketCategory === 'stocks' && STOCK_PAIRS
                                        .filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase()))
                                        .map((item, i) => {
                                            const pd = nonCryptoPrices[item.yahooSymbol]
                                            const isActive = activeYahooSymbol === item.yahooSymbol
                                            return (
                                                <div
                                                    key={i}
                                                    className={`tm-pair-dropdown-item ${isActive ? 'active' : ''}`}
                                                    onClick={() => { localStorage.setItem('dw_spot_yahoo_symbol', item.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', item.name); localStorage.setItem('dw_spot_noncrypto_symbol', item.symbol); setActiveYahooSymbol(item.yahooSymbol); setActiveNonCryptoName(item.name); setActiveNonCryptoSymbol(item.symbol); setShowMobilePairDropdown(false); setPairSearch('') }}
                                                >
                                                    <div className="tm-pair-dropdown-name">
                                                        <AssetIcon src={stockLogo(item.symbol)} label={item.symbol} size={18} radius={4} />
                                                        <div style={{ marginLeft: 6 }}>
                                                            <span className="tm-pair-dropdown-symbol">{item.symbol}</span>
                                                            <div style={{ fontSize: 10, color: 'var(--ui-text-muted)' }}>{item.name}</div>
                                                        </div>
                                                    </div>
                                                    <span className="tm-pair-dropdown-price">{pd ? `$${pd.price.toFixed(2)}` : 'Loading...'}</span>
                                                    <span className={`tm-pair-dropdown-change ${!pd || pd.change >= 0 ? 'positive' : 'negative'}`}>{pd ? `${pd.change >= 0 ? '+' : ''}${pd.change.toFixed(2)}%` : '...'}</span>
                                                </div>
                                            )
                                        })
                                    }
                                    {/* ── Commodities ── */}
                                    {marketCategory === 'commodities' && COMMODITY_PAIRS
                                        .filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase()))
                                        .map((item, i) => {
                                            const pd = nonCryptoPrices[item.yahooSymbol]
                                            const isActive = activeYahooSymbol === item.yahooSymbol
                                            return (
                                                <div
                                                    key={i}
                                                    className={`tm-pair-dropdown-item ${isActive ? 'active' : ''}`}
                                                    onClick={() => { localStorage.setItem('dw_spot_yahoo_symbol', item.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', item.name); localStorage.setItem('dw_spot_noncrypto_symbol', item.symbol); setActiveYahooSymbol(item.yahooSymbol); setActiveNonCryptoName(item.name); setActiveNonCryptoSymbol(item.symbol); setShowMobilePairDropdown(false); setPairSearch('') }}
                                                >
                                                    <div className="tm-pair-dropdown-name">
                                                        <span style={{ width: 18, height: 18, borderRadius: '50%', background: item.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>{item.emoji}</span>
                                                        <div style={{ marginLeft: 6 }}>
                                                            <span className="tm-pair-dropdown-symbol">{item.symbol}</span>
                                                            <div style={{ fontSize: 10, color: 'var(--ui-text-muted)' }}>{item.name}</div>
                                                        </div>
                                                    </div>
                                                    <span className="tm-pair-dropdown-price">{pd ? `$${pd.price.toFixed(2)}` : 'Loading...'}</span>
                                                    <span className={`tm-pair-dropdown-change ${!pd || pd.change >= 0 ? 'positive' : 'negative'}`}>{pd ? `${pd.change >= 0 ? '+' : ''}${pd.change.toFixed(2)}%` : '...'}</span>
                                                </div>
                                            )
                                        })
                                    }
                                    {/* ── Forex ── */}
                                    {marketCategory === 'forex' && FOREX_PAIRS
                                        .filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase()))
                                        .map((item, i) => {
                                            const pd = nonCryptoPrices[item.yahooSymbol]
                                            const isActive = activeYahooSymbol === item.yahooSymbol
                                            const closed = !isMarketOpen('forex', item.symbol)
                                            return (
                                                <div
                                                    key={i}
                                                    className={`tm-pair-dropdown-item ${isActive ? 'active' : ''}`}
                                                    onClick={() => { localStorage.setItem('dw_spot_yahoo_symbol', item.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', item.name); localStorage.setItem('dw_spot_noncrypto_symbol', item.symbol); setActiveYahooSymbol(item.yahooSymbol); setActiveNonCryptoName(item.name); setActiveNonCryptoSymbol(item.symbol); setShowMobilePairDropdown(false); setPairSearch('') }}
                                                >
                                                    <div className="tm-pair-dropdown-name">
                                                        <span style={{ width: 18, height: 18, borderRadius: '50%', background: item.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>{item.emoji}</span>
                                                        <div style={{ marginLeft: 6 }}>
                                                            <span className="tm-pair-dropdown-symbol">{item.symbol}{closed && <span style={{ marginLeft: 6, fontSize: 9, color: '#EF4444' }}>Closed</span>}</span>
                                                            <div style={{ fontSize: 10, color: 'var(--ui-text-muted)' }}>{item.name}</div>
                                                        </div>
                                                    </div>
                                                    <span className="tm-pair-dropdown-price">{pd ? pd.price.toFixed(4) : 'Loading...'}</span>
                                                    <span className={`tm-pair-dropdown-change ${!pd || pd.change >= 0 ? 'positive' : 'negative'}`}>{pd ? `${pd.change >= 0 ? '+' : ''}${pd.change.toFixed(2)}%` : '...'}</span>
                                                </div>
                                            )
                                        })
                                    }
                                    {/* ── Indices ── */}
                                    {marketCategory === 'indices' && INDEX_PAIRS
                                        .filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase()))
                                        .map((item, i) => {
                                            const pd = nonCryptoPrices[item.yahooSymbol]
                                            const isActive = activeYahooSymbol === item.yahooSymbol
                                            const closed = !isMarketOpen('indices', item.symbol)
                                            return (
                                                <div
                                                    key={i}
                                                    className={`tm-pair-dropdown-item ${isActive ? 'active' : ''}`}
                                                    onClick={() => { localStorage.setItem('dw_spot_yahoo_symbol', item.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', item.name); localStorage.setItem('dw_spot_noncrypto_symbol', item.symbol); setActiveYahooSymbol(item.yahooSymbol); setActiveNonCryptoName(item.name); setActiveNonCryptoSymbol(item.symbol); setShowMobilePairDropdown(false); setPairSearch('') }}
                                                >
                                                    <div className="tm-pair-dropdown-name">
                                                        <span style={{ width: 18, height: 18, borderRadius: '50%', background: item.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>{item.emoji}</span>
                                                        <div style={{ marginLeft: 6 }}>
                                                            <span className="tm-pair-dropdown-symbol">{item.symbol}{closed && <span style={{ marginLeft: 6, fontSize: 9, color: '#EF4444' }}>Closed</span>}</span>
                                                            <div style={{ fontSize: 10, color: 'var(--ui-text-muted)' }}>{item.name}</div>
                                                        </div>
                                                    </div>
                                                    <span className="tm-pair-dropdown-price">{pd ? `$${pd.price.toFixed(2)}` : 'Loading...'}</span>
                                                    <span className={`tm-pair-dropdown-change ${!pd || pd.change >= 0 ? 'positive' : 'negative'}`}>{pd ? `${pd.change >= 0 ? '+' : ''}${pd.change.toFixed(2)}%` : '...'}</span>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        )}
                        {showMobilePairDropdown && (
                            <div className="tm-pair-dropdown-backdrop" onClick={() => { setShowMobilePairDropdown(false); setPairSearch('') }} />
                        )}
                    </div>
                    <div className="tm-scroll-content">
                        <div className="tm-price-section-v3">
                            <div className="tm-price-main-v3">
                                <h1 className="tm-big-price-v3">${formatPrice(currentPrice)}</h1>
                                <span className={`tm-price-change-v3 ${change24h >= 0 ? '' : 'negative'}`}>
                                    {change24h >= 0 ? '+' : ''}{change24h.toFixed(2)}%
                                </span>
                            </div>
                            <div className="tm-stats-grid-v3">
                                <div className="tm-stat-row-v3">
                                    <div className="tm-stat-item-v3"><span className="tm-stat-lbl">24h High</span><span className="tm-stat-val">${formatPrice(high24h)}</span></div>
                                    <div className="tm-stat-item-v3"><span className="tm-stat-lbl">24h Vol ({selectedCoin})</span><span className="tm-stat-val">{volume24h > 1e6 ? `${(volume24h / 1e6).toFixed(2)}M` : volume24h > 0 ? volume24h.toLocaleString(undefined, {maximumFractionDigits:2}) : '...'}</span></div>
                                </div>
                                <div className="tm-stat-row-v3">
                                    <div className="tm-stat-item-v3"><span className="tm-stat-lbl">24h Low</span><span className="tm-stat-val">${formatPrice(low24h)}</span></div>
                                    <div className="tm-stat-item-v3"><span className="tm-stat-lbl">24h Vol (USDT)</span><span className="tm-stat-val">{quoteVol24h > 1e9 ? `${(quoteVol24h / 1e9).toFixed(2)}B` : quoteVol24h > 1e6 ? `${(quoteVol24h / 1e6).toFixed(2)}M` : quoteVol24h > 0 ? quoteVol24h.toLocaleString(undefined,{maximumFractionDigits:0}) : '...'}</span></div>
                                </div>
                            </div>
                        </div>
                        <div className="tm-info-banner-v3">
                            <div className="tm-info-left-v3">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00C0A3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></svg>
                                <span>Mark Price</span>
                                <span className="tm-val-white">${formatPrice(currentPrice)}</span>
                            </div>
                            <div className="tm-info-right-v3"><span>Funding</span><span className={fundingRate >= 0 ? 'tm-val-white' : 'tm-val-red'}>{fundingRate >= 0 ? '+' : ''}{fundingRate.toFixed(4)}%</span></div>
                        </div>
                        <>
                        {isMobileView && (
                        <TradingChart
                            pair={selectedPair}
                            marketCategory={marketCategory}
                            yahooSymbol={activeYahooSymbol}
                            seedPrice={nonCryptoPrices[activeYahooSymbol || '']?.price || NON_CRYPTO_BASE[activeYahooSymbol || '']}
                            currentPrice={currentPrice}
                            timeframe={selectedTimeframe}
                            timeframeOptions={timeframes}
                            onTimeframeChange={setSelectedTimeframe}
                            lineType={lineType as any}
                            onIndicatorsClick={() => setShowOptionsOverlay(true)}
                            onSettingsClick={() => setShowLineOverlay(true)}
                            isMobile
                            onOhlcChange={setOhlcData}
                        />
                        )}                        {marketCategory === 'crypto' && (
                        <div className="tm-ob-section-v3">
                            <div className="tm-ob-tabs-v3">
                                <div className={`tm-ob-tab-v3 ${mobileObTab === 'orderbook' ? 'active' : ''}`} onClick={() => setMobileObTab('orderbook')}>Order book</div>
                                <div className={`tm-ob-tab-v3 ${mobileObTab === 'trades' ? 'active' : ''}`} onClick={() => setMobileObTab('trades')}>Market Trades</div>
                            </div>
                            <div className="tm-ob-grid-v3">
                                <div className="tm-ob-col-v3">
                                    {bidOrders.slice(0, 10).map((order, i) => (
                                        <div key={i} className="tm-ob-row-v3">
                                            <span className="tm-ob-p-v3">{formatPrice(order.price)}</span>
                                            <span className="tm-ob-v-v3 green">{order.amount.toFixed(5)}</span>
                                            <div className="tm-ob-depth-v3 green" style={{ width: `${Math.min(order.total / (bidOrders[0]?.total || 1) * 80, 100)}%` }}></div>
                                        </div>
                                    ))}
                                </div>
                                <div className="tm-ob-col-v3">
                                    {askOrders.slice(0, 10).map((order, i) => (
                                        <div key={i} className="tm-ob-row-v3 text-right">
                                            <span className="tm-ob-p-v3">{formatPrice(order.price)}</span>
                                            <span className="tm-ob-v-v3 red">{order.amount.toFixed(5)}</span>
                                            <div className="tm-ob-depth-v3 red" style={{ width: `${Math.min(order.total / (askOrders[0]?.total || 1) * 80, 100)}%` }}></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        )}
                        </>
                        <div style={{ padding: '16px 16px 20px', marginTop: 8, borderTop: '1px solid #1a1a24' }}>
                            {/* Available + Max */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={{ color: 'var(--ui-text-muted)', fontSize: 12 }}>Available</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ color: 'var(--ui-text-primary)', fontSize: 12, fontWeight: 600 }}>{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${userBalance.toFixed(2)} USDT`}</span>
                                    <span style={{ color: '#00C0A3', fontSize: 16, cursor: 'pointer', lineHeight: 1 }} onClick={() => navigate('/dashboard/deposit')}>+</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                <span style={{ color: 'var(--ui-text-muted)', fontSize: 12 }}>Max buy</span>
                                <span style={{ color: 'var(--ui-text-secondary)', fontSize: 12 }}>{currentPrice > 0 ? (userBalance / currentPrice).toFixed(6) : (high24h > 0 ? (0 / high24h).toFixed(6) : '0.000000')} {selectedCoin}</span>
                            </div>

                            {/* Order Type Toggle */}
                            <div style={{ display: 'flex', background: 'var(--ui-bg-elevated)', borderRadius: 8, padding: 3, marginBottom: 12, gap: 2 }}>
                                {(['limit', 'market'] as const).map(t => (
                                    <button key={t} onClick={() => setOrderType(t)}
                                        style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                            background: orderType === t ? '#2a2a3a' : 'transparent',
                                            color: orderType === t ? '#fff' : '#71717A' }}>
                                        {t.charAt(0).toUpperCase() + t.slice(1)}
                                    </button>
                                ))}
                            </div>

                            {/* Price Field */}
                            {orderType === 'limit' && (
                                <div style={{ marginBottom: 10 }}>
                                    {/* Market rate quick-fill */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                        <span style={{ fontSize: 11, color: 'var(--ui-text-muted)' }}>
                                            Market: <span style={{ color: 'var(--ui-text-primary)', fontVariantNumeric: 'tabular-nums' }}>${currentPrice > 0 ? formatLivePrice(currentPrice) : '—'}</span>
                                        </span>
                                        <button
                                            onClick={() => currentPrice > 0 && setTradePrice(currentPrice.toFixed(currentPrice >= 1000 ? 2 : currentPrice >= 1 ? 4 : 6))}
                                            style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 5, border: '1px solid #1CD4A7', background: 'rgba(28,212,167,0.1)', color: '#1CD4A7', cursor: 'pointer', letterSpacing: '0.01em' }}
                                        >
                                            Use Market Rate
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', background: 'var(--ui-bg-elevated)', borderRadius: 10, padding: '10px 12px', alignItems: 'center', border: '1px solid #2a2a34', gap: 8 }}>
                                        <button onClick={() => { const p = parseFloat(tradePrice) || currentPrice; if (p > 0.01) setTradePrice((p - (p >= 1000 ? 1 : 0.01)).toFixed(p >= 1000 ? 2 : 4)) }}
                                            style={{ background: 'var(--ui-bg-elevated)', border: 'none', color: 'var(--ui-text-primary)', borderRadius: 6, width: 28, height: 28, fontSize: 18, cursor: 'pointer', flexShrink: 0 }}>−</button>
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <span style={{ color: 'var(--ui-text-muted)', fontSize: 10, marginBottom: 2 }}>Price (USDT)</span>
                                            <input type="text" inputMode="decimal" value={tradePrice} onChange={e => handleNumericInput(e.target.value, setTradePrice)}
                                                placeholder={currentPrice > 0 ? formatLivePrice(currentPrice) : '0.00'}
                                                style={{ background: 'transparent', border: 'none', color: 'var(--ui-text-primary)', fontSize: 15, outline: 'none', textAlign: 'center', width: '100%', fontWeight: 600 }} />
                                        </div>
                                        <button onClick={() => { const p = parseFloat(tradePrice) || currentPrice; setTradePrice((p + (p >= 1000 ? 1 : 0.01)).toFixed(p >= 1000 ? 2 : 4)) }}
                                            style={{ background: 'var(--ui-bg-elevated)', border: 'none', color: '#00C0A3', borderRadius: 6, width: 28, height: 28, fontSize: 18, cursor: 'pointer', flexShrink: 0 }}>+</button>
                                    </div>
                                </div>
                            )}
                            {orderType === 'market' && (
                                <div style={{ marginBottom: 10, display: 'flex', background: 'var(--ui-bg-elevated)', borderRadius: 10, padding: '10px 12px', alignItems: 'center', border: '1px solid #2a2a34', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--ui-text-muted)', fontSize: 12 }}>Price (USDT)</span>
                                    <span style={{ color: '#10b981', fontSize: 15, fontWeight: 600 }}>{currentPrice > 0 ? formatLivePrice(currentPrice) : high24h > 0 ? formatLivePrice(high24h) : 'Loading...'}</span>
                                </div>
                            )}

                            {/* Amount BTC Field */}
                            <div style={{ marginBottom: 10 }}>
                                <div style={{ display: 'flex', background: 'var(--ui-bg-elevated)', borderRadius: 10, padding: '10px 12px', alignItems: 'center', border: '1px solid #2a2a34', gap: 8 }}>
                                    <button onClick={() => { const a = parseFloat(tradeAmount) || 0; if (a > 0.0001) setTradeAmount((a - 0.0001).toFixed(6)) }}
                                        style={{ background: 'var(--ui-bg-elevated)', border: 'none', color: 'var(--ui-text-primary)', borderRadius: 6, width: 28, height: 28, fontSize: 18, cursor: 'pointer', flexShrink: 0 }}>−</button>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <span style={{ color: 'var(--ui-text-muted)', fontSize: 10, marginBottom: 2 }}>Amount ({selectedCoin})</span>
                                        <input type="text" inputMode="decimal" value={tradeAmount} onChange={e => handleAmountChange(e.target.value)}
                                            placeholder="0.00"
                                            style={{ background: 'transparent', border: 'none', color: 'var(--ui-text-primary)', fontSize: 15, outline: 'none', textAlign: 'center', width: '100%', fontWeight: 600 }} />
                                    </div>
                                    <button onClick={() => { const a = parseFloat(tradeAmount) || 0; setTradeAmount((a + 0.0001).toFixed(6)) }}
                                        style={{ background: 'var(--ui-bg-elevated)', border: 'none', color: '#00C0A3', borderRadius: 6, width: 28, height: 28, fontSize: 18, cursor: 'pointer', flexShrink: 0 }}>+</button>
                                </div>
                            </div>

                            {/* Percentage Slider */}
                            {(() => {
                                const pctVal = currentPrice > 0 && userBalance > 0 ? Math.min(100, Math.round((parseFloat(tradeAmount) || 0) * currentPrice / userBalance * 100)) : 0
                                const setPct = (p: number) => { if (currentPrice > 0 && userBalance > 0) setTradeAmount(((userBalance * p / 100) / currentPrice).toFixed(6)); else if (currentPrice > 0) setTradeAmount('0.000000') }
                                return (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ position: 'relative', height: 44, display: 'flex', alignItems: 'center' }}>
                                        <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: 'var(--ui-bg-elevated)', borderRadius: 2, top: '50%', transform: 'translateY(-50%)' }}>
                                            <div style={{ position: 'absolute', left: 0, width: `${pctVal}%`, height: '100%', background: '#00C0A3', borderRadius: 2 }} />
                                            <div style={{ position: 'absolute', left: `${pctVal}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 16, height: 16, borderRadius: '50%', background: '#00C0A3', border: '2px solid #fff', boxShadow: '0 0 6px rgba(0,192,163,0.7)', pointerEvents: 'none', zIndex: 1 }} />
                                            {[0,25,50,75,100].map(n => <div key={n} style={{ position: 'absolute', left: `${n}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 4, height: 4, borderRadius: '50%', background: pctVal >= n ? '#00C0A3' : '#3a3a4a', pointerEvents: 'none' }} />)}
                                        </div>
                                        <input type="range" min={0} max={100} step={1} value={pctVal}
                                            style={{ position: 'absolute', width: '100%', height: 44, opacity: 0, cursor: 'pointer', margin: 0, padding: 0, zIndex: 2, WebkitAppearance: 'none', appearance: 'none' }}
                                            onChange={e => setPct(Number(e.target.value))} />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                                        {[0,25,50,75,100].map(n => <span key={n} onClick={() => setPct(n)} style={{ color: pctVal >= n ? '#00C0A3' : '#52525b', fontSize: 10, cursor: 'pointer' }}>{n}%</span>)}
                                    </div>
                                </div>
                                )
                            })()}

                            {/* Total USDT Field */}
                            <div style={{ display: 'flex', background: 'var(--ui-bg-elevated)', borderRadius: 10, padding: '10px 12px', alignItems: 'center', border: '1px solid #2a2a34', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span style={{ color: 'var(--ui-text-muted)', fontSize: 12, flexShrink: 0 }}>Total (USDT)</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={tradeTotal}
                                    onChange={e => handleTotalChange(e.target.value)}
                                    onFocus={() => { lastEditedField.current = 'total' }}
                                    placeholder="0.00"
                                    style={{ background: 'transparent', border: 'none', color: 'var(--ui-text-primary)', fontSize: 15, fontWeight: 600, outline: 'none', textAlign: 'right', width: '100%', fontVariantNumeric: 'tabular-nums' }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="tm-action-bar-v3">
                        <button className="tm-buy-btn-v3" onClick={() => handleTrade('buy')} disabled={submittingSide !== null || currentPrice <= 0 || marketClosed}>{submittingSide === 'buy' ? 'Processing...' : marketClosed ? 'Market Closed' : currentPrice <= 0 ? 'Loading price...' : 'Buy'}</button>
                        <button className="tm-sell-btn-v3" onClick={() => handleTrade('sell')} disabled={submittingSide !== null || currentPrice <= 0 || marketClosed}>{submittingSide === 'sell' ? 'Processing...' : marketClosed ? 'Market Closed' : currentPrice <= 0 ? 'Loading price...' : 'Sell'}</button>
                    </div>

                    {/* Mobile Open Orders */}
                    <div style={{ borderTop: '1px solid var(--ui-border)', marginTop: 8 }}>
                        <div style={{ padding: '10px 16px 4px', fontSize: 13, fontWeight: 600, color: 'var(--ui-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            Open Orders <span style={{ fontSize: 11, background: 'var(--ui-bg-elevated)', borderRadius: 10, padding: '1px 7px', color: 'var(--ui-text-primary)' }}>{openTradesList.length}</span>
                        </div>
                        {openTradesList.length === 0 ? (
                            <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--ui-text-secondary)', textAlign: 'center' }}>No open orders</div>
                        ) : (
                            <div style={{ padding: '0 16px 12px' }}>
                                {openTradesList.map(trade => {
                                    const isBuy = trade.side === 'buy' || trade.side === 'long'
                                    const isPending = trade.status === 'pending'
                                    const pnl = isPending ? 0 : computeLivePnL(trade)
                                    const pnlPositive = pnl >= 0
                                    const total = (trade.amount || 0) * (trade.entryPrice || trade.limitPrice || 0)
                                    return (
                                    <div key={trade._id} style={{ padding: '10px 0', borderBottom: '1px solid var(--ui-border)' }}>
                                            {/* Row 1: pair + badges (left), P&L (right) */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ui-text-primary)' }}>{trade.asset}/USDT</span>
                                                    <span style={{ background: isBuy ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: isBuy ? '#10b981' : '#ef4444', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{isBuy ? 'Buy' : 'Sell'}</span>
                                                    {isPending && <span style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>Pending</span>}
                                                </div>
                                                {!isPending && (
                                                    <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: pnlPositive ? '#10b981' : '#ef4444' }}>{pnlPositive ? '+' : ''}${pnl.toFixed(2)}</span>
                                                )}
                                            </div>
                                            {/* Row 2: Amount / Price / Total — compact inline */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginBottom: 8 }}>
                                                <div>
                                                    <span style={{ fontSize: 10, color: 'var(--ui-text-muted)' }}>Amt </span>
                                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ui-text-primary)' }}>{formatAmount(trade.amount)}</span>
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: 10, color: 'var(--ui-text-muted)' }}>{isPending ? 'Limit ' : 'Entry '}</span>
                                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ui-text-primary)' }}>${formatPrice(isPending ? (trade.limitPrice ?? trade.entryPrice) : trade.entryPrice)}</span>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <span style={{ fontSize: 10, color: 'var(--ui-text-muted)' }}>Total </span>
                                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ui-text-primary)' }}>${total.toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleCloseTrade(trade._id)}
                                                style={{ width: '100%', background: isPending ? 'var(--ui-bg-elevated)' : 'linear-gradient(135deg,#ef4444,#dc2626)', color: isPending ? 'var(--ui-text-primary)' : '#fff', border: isPending ? '1px solid var(--ui-border)' : 'none', borderRadius: 6, padding: '7px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                            >
                                                {isPending ? 'Cancel Order' : 'Close'}
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                    {showOptionsOverlay && (
                        <div className="tm-overlay-v3" onClick={() => setShowOptionsOverlay(false)}>
                            <div className="tm-overlay-content-v3" onClick={e => e.stopPropagation()}>
                                <div className="tm-overlay-header-v3"><span className="tm-overlay-title">Options</span><button className="tm-close-overlay" onClick={() => setShowOptionsOverlay(false)}>{"\u2715"}</button></div>
                                <div className="tm-overlay-list-v3">
                                    {['Quick order', 'Open order', 'TP/SL', 'Positions', 'Order history'].map(opt => (
                                        <div key={opt} className="tm-overlay-item-v3" onClick={() => { setOptionType(opt); setShowOptionsOverlay(false) }}>
                                            <div className={`tm-radio-v3 ${optionType === opt ? 'active' : ''}`}></div><span>{opt}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    {showLineOverlay && (
                        <div className="tm-overlay-v3" onClick={() => setShowLineOverlay(false)}>
                            <div className="tm-overlay-content-v3" onClick={e => e.stopPropagation()}>
                                <div className="tm-overlay-header-v3"><span className="tm-overlay-title">Line</span><button className="tm-close-overlay" onClick={() => setShowLineOverlay(false)}>{"\u2715"}</button></div>
                                <div className="tm-overlay-list-v3">
                                    {['Candles', 'Bars', 'Area', 'Heiken Aishi', 'Baseline', 'High low', 'Column'].map(type => (
                                        <div key={type} className="tm-overlay-item-v3" onClick={() => { setLineType(type); setShowLineOverlay(false) }}>
                                            <div className={`tm-radio-v3 ${lineType === type ? 'active' : ''}`}></div><span>{type}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="desktop-only">
                <div className="spot-terminal">
                    <div className="spot-main-grid-v2">
                        <div className="spot-pairs-sidebar-v2">
                            {/* Selected asset header with real logo */}
                            <div className="spot-pair-header-v2">
                                <div className="spot-pair-header-left">
                                    <div className="spot-pair-icon-v2" style={{ overflow: 'hidden', borderRadius: '50%', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {marketCategory === 'crypto'
                                            ? <AssetIcon src={coinLogo(selectedCoin)} src2={coinLogoFallback(selectedCoin)} label={selectedCoin} size={32} radius="50%" />
                                            : marketCategory === 'stocks'
                                                ? <AssetIcon src={stockLogo(activeYahooSymbol)} label={activeYahooSymbol} size={32} radius={6} />
                                                : (() => { const c = CATEGORY_PAIRS[marketCategory].find(p => p.yahooSymbol === activeYahooSymbol); return <span style={{ width: 32, height: 32, borderRadius: '50%', background: c?.color ?? '#4a4a6a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{c?.emoji ?? '📦'}</span> })()
                                        }
                                    </div>
                                    <div className="spot-pair-info-v2">
                                        <span className="spot-pair-title-v2">{marketCategory === 'crypto' ? <>{selectedCoin} <span className="spot-pair-slash">/USDT</span></> : activeNonCryptoName}</span>
                                        <span className="spot-pair-subtitle-v2">
                                            {marketCategory === 'crypto' ? COIN_NAMES[selectedCoin] : CATEGORY_LABELS[marketCategory]}
                                            {marketCategory !== 'crypto' && !isMarketOpen(marketCategory, activeNonCryptoSymbol) && (
                                                <span style={{ marginLeft: 6, color: '#EF4444' }}>· Market Closed</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className="spot-pair-header-right">
                                    <span className="spot-pair-price-main">${formatPrice(currentPrice)}</span>
                                    <span className={`spot-pair-price-sub ${change24h >= 0 ? 'positive' : 'negative'}`}>{change24h >= 0 ? '+' : ''}{change24h.toFixed(2)}%</span>
                                </div>
                            </div>
                            {/* Search bar */}
                            <div className="spot-pairs-search-v2">
                                <Search size={16} className="spot-search-icon-v2" />
                                <input type="text" placeholder="Search" className="spot-search-input-v2" value={pairSearch} onChange={e => setPairSearch(e.target.value)} />
                            </div>
                            {/* Category tabs — after search bar */}
                            <div style={{ display: 'flex', borderBottom: '1px solid #2a2a2a', marginBottom: 2 }}>
                                {(['crypto', 'stocks', 'commodities', 'forex', 'indices'] as const).map(cat => (
                                    <button key={cat} onClick={() => { setPairSearch(''); localStorage.setItem('dw_spot_category', cat); setMarketCategory(cat); if (cat !== 'crypto') { const first = CATEGORY_PAIRS[cat][0]; localStorage.setItem('dw_spot_yahoo_symbol', first.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', first.name); localStorage.setItem('dw_spot_noncrypto_symbol', first.symbol); setActiveYahooSymbol(first.yahooSymbol); setActiveNonCryptoName(first.name); setActiveNonCryptoSymbol(first.symbol) } }} style={{ flex: 1, background: marketCategory === cat ? '#2563eb' : 'transparent', color: marketCategory === cat ? '#fff' : '#9ca3af', border: 'none', padding: '7px 0', fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', letterSpacing: 0.4, borderRadius: 0 }}>
                                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                    </button>
                                ))}
                            </div>
                            {marketCategory === 'crypto' && (
                                <div className="spot-pairs-tabs-v2">
                                    <button className={`spot-pairs-tab-v2 ${pairsTab === 'usdt' ? 'active' : ''}`} onClick={() => setPairsTab('usdt')}><Star size={12} /> USDT</button>
                                    <button className={`spot-pairs-tab-v2 ${pairsTab === 'grid' ? 'active' : ''}`} onClick={() => setPairsTab('grid')}>Grid</button>
                                </div>
                            )}
                            <div className="spot-pairs-header-v2">
                                <span className="spot-pairs-col-v2">Name <ChevronDown size={10} /></span>
                                <span className="spot-pairs-col-v2">Price <ChevronDown size={10} /></span>
                                <span className="spot-pairs-col-v2">24h<br />change <ChevronDown size={10} /></span>
                            </div>
                            <div className="spot-pairs-list-v2">
                                {/* ── Crypto ── */}
                                {marketCategory === 'crypto' && tradingPairs.filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase())).map((pair, i) => (
                                    <div key={i} className={`spot-pairs-row-v2 ${pair.fullPair === selectedPair ? 'active' : ''}`} onClick={() => handleSelectPair(pair.fullPair)} style={{ cursor: 'pointer' }}>
                                        <div className="spot-pairs-name-v2" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <AssetIcon src={coinLogo(pair.symbol)} src2={coinLogoFallback(pair.symbol)} label={pair.symbol} size={18} radius="50%" />
                                            <div><span className="spot-symbol-v2">{pair.symbol}</span><span className="spot-pair-suffix-v2">/{pair.pair}</span></div>
                                        </div>
                                        <span className="spot-pairs-price-v2">{pair.priceStr}</span>
                                        <span className={`spot-pairs-change-v2 ${pair.positive ? 'positive' : 'negative'}`}>{pair.change}</span>
                                    </div>
                                ))}
                                {/* ── Stocks ── */}
                                {marketCategory === 'stocks' && STOCK_PAIRS.filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase())).map((item, i) => {
                                    const pd = nonCryptoPrices[item.yahooSymbol]
                                    const isActive = activeYahooSymbol === item.yahooSymbol
                                    return (
                                        <div key={i} className={`spot-pairs-row-v2 ${isActive ? 'active' : ''}`} onClick={() => { localStorage.setItem('dw_spot_yahoo_symbol', item.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', item.name); localStorage.setItem('dw_spot_noncrypto_symbol', item.symbol); setActiveYahooSymbol(item.yahooSymbol); setActiveNonCryptoName(item.name); setActiveNonCryptoSymbol(item.symbol) }} style={{ cursor: 'pointer' }}>
                                            <div className="spot-pairs-name-v2" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <AssetIcon src={stockLogo(item.symbol)} label={item.symbol} size={20} radius={4} />
                                                <div><span className="spot-symbol-v2">{item.symbol}</span></div>
                                            </div>
                                            <span className="spot-pairs-price-v2">{pd ? `$${pd.price.toFixed(2)}` : '...'}</span>
                                            <span className={`spot-pairs-change-v2 ${!pd || pd.change >= 0 ? 'positive' : 'negative'}`}>{pd ? `${pd.change >= 0 ? '+' : ''}${pd.change.toFixed(2)}%` : '...'}</span>
                                        </div>
                                    )
                                })}
                                {/* ── Commodities ── */}
                                {marketCategory === 'commodities' && COMMODITY_PAIRS.filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase())).map((item, i) => {
                                    const pd = nonCryptoPrices[item.yahooSymbol]
                                    const isActive = activeYahooSymbol === item.yahooSymbol
                                    return (
                                        <div key={i} className={`spot-pairs-row-v2 ${isActive ? 'active' : ''}`} onClick={() => { localStorage.setItem('dw_spot_yahoo_symbol', item.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', item.name); localStorage.setItem('dw_spot_noncrypto_symbol', item.symbol); setActiveYahooSymbol(item.yahooSymbol); setActiveNonCryptoName(item.name); setActiveNonCryptoSymbol(item.symbol) }} style={{ cursor: 'pointer' }}>
                                            <div className="spot-pairs-name-v2" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <span style={{ width: 20, height: 20, borderRadius: '50%', background: item.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>{item.emoji}</span>
                                                <div><span className="spot-symbol-v2">{item.symbol}</span></div>
                                            </div>
                                            <span className="spot-pairs-price-v2">{pd ? `$${pd.price.toFixed(2)}` : '...'}</span>
                                            <span className={`spot-pairs-change-v2 ${!pd || pd.change >= 0 ? 'positive' : 'negative'}`}>{pd ? `${pd.change >= 0 ? '+' : ''}${pd.change.toFixed(2)}%` : '...'}</span>
                                        </div>
                                    )
                                })}
                                {/* ── Forex ── */}
                                {marketCategory === 'forex' && FOREX_PAIRS.filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase())).map((item, i) => {
                                    const pd = nonCryptoPrices[item.yahooSymbol]
                                    const isActive = activeYahooSymbol === item.yahooSymbol
                                    const closed = !isMarketOpen('forex', item.symbol)
                                    return (
                                        <div key={i} className={`spot-pairs-row-v2 ${isActive ? 'active' : ''}`} onClick={() => { localStorage.setItem('dw_spot_yahoo_symbol', item.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', item.name); localStorage.setItem('dw_spot_noncrypto_symbol', item.symbol); setActiveYahooSymbol(item.yahooSymbol); setActiveNonCryptoName(item.name); setActiveNonCryptoSymbol(item.symbol) }} style={{ cursor: 'pointer' }}>
                                            <div className="spot-pairs-name-v2" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <span style={{ width: 20, height: 20, borderRadius: '50%', background: item.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>{item.emoji}</span>
                                                <div><span className="spot-symbol-v2">{item.symbol}</span>{closed && <span style={{ marginLeft: 5, fontSize: 9, color: '#EF4444' }}>Closed</span>}</div>
                                            </div>
                                            <span className="spot-pairs-price-v2">{pd ? pd.price.toFixed(4) : '...'}</span>
                                            <span className={`spot-pairs-change-v2 ${!pd || pd.change >= 0 ? 'positive' : 'negative'}`}>{pd ? `${pd.change >= 0 ? '+' : ''}${pd.change.toFixed(2)}%` : '...'}</span>
                                        </div>
                                    )
                                })}
                                {/* ── Indices ── */}
                                {marketCategory === 'indices' && INDEX_PAIRS.filter(p => !pairSearch || p.symbol.toLowerCase().includes(pairSearch.toLowerCase()) || p.name.toLowerCase().includes(pairSearch.toLowerCase())).map((item, i) => {
                                    const pd = nonCryptoPrices[item.yahooSymbol]
                                    const isActive = activeYahooSymbol === item.yahooSymbol
                                    const closed = !isMarketOpen('indices', item.symbol)
                                    return (
                                        <div key={i} className={`spot-pairs-row-v2 ${isActive ? 'active' : ''}`} onClick={() => { localStorage.setItem('dw_spot_yahoo_symbol', item.yahooSymbol); localStorage.setItem('dw_spot_noncrypto_name', item.name); localStorage.setItem('dw_spot_noncrypto_symbol', item.symbol); setActiveYahooSymbol(item.yahooSymbol); setActiveNonCryptoName(item.name); setActiveNonCryptoSymbol(item.symbol) }} style={{ cursor: 'pointer' }}>
                                            <div className="spot-pairs-name-v2" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <span style={{ width: 20, height: 20, borderRadius: '50%', background: item.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>{item.emoji}</span>
                                                <div><span className="spot-symbol-v2">{item.symbol}</span>{closed && <span style={{ marginLeft: 5, fontSize: 9, color: '#EF4444' }}>Closed</span>}</div>
                                            </div>
                                            <span className="spot-pairs-price-v2">{pd ? `$${pd.price.toFixed(2)}` : '...'}</span>
                                            <span className={`spot-pairs-change-v2 ${!pd || pd.change >= 0 ? 'positive' : 'negative'}`}>{pd ? `${pd.change >= 0 ? '+' : ''}${pd.change.toFixed(2)}%` : '...'}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        <div className="spot-center-panel-v2">
                            <div className="spot-chart-section-v2">
                                <div className="spot-chart-header-v2">
                                    <div className="spot-chart-tabs-v2">
                                        <span className={`spot-chart-tab-v2 ${chartTab === 'chart' ? 'active' : ''}`} onClick={() => setChartTab('chart')}>Chart</span>
                                        <span className={`spot-chart-tab-v2 ${chartTab === 'info' ? 'active' : ''}`} onClick={() => setChartTab('info')}>Info</span>
                                    </div>
                                    <div className="spot-view-tabs-v2">
                                        <span className="spot-view-tab-v2 active">Original</span>
                                        <span className="spot-view-tab-v2">Depth</span>
                                    </div>
                                </div>
                                <div className="spot-chart-container-v2">
                                    {!isMobileView && (
                                    <TradingChart
                                        pair={selectedPair}
                                        marketCategory={marketCategory}
                                        yahooSymbol={activeYahooSymbol}
                                        seedPrice={nonCryptoPrices[activeYahooSymbol || '']?.price || NON_CRYPTO_BASE[activeYahooSymbol || '']}
                                        currentPrice={currentPrice}
                                        timeframe={selectedTimeframe}
                                        timeframeOptions={timeframes}
                                        onTimeframeChange={setSelectedTimeframe}
                                        lineType={lineType as any}
                                        onIndicatorsClick={() => setShowOptionsOverlay(true)}
                                        onSettingsClick={() => setShowLineOverlay(true)}
                                        height={420}
                                        onOhlcChange={setOhlcData}
                                    />
                                    )}
                                </div>
                            </div>
                            <div className="spot-trade-form-section-v2">
                                <div className="spot-trade-type-tabs-v2">
                                    <span className={`spot-trade-type-tab-v2 ${tradeType === 'spot' ? 'active' : ''}`} onClick={() => setTradeType('spot')}>Spot</span>
                                    <span className={`spot-trade-type-tab-v2 ${tradeType === 'grid' ? 'active' : ''}`} onClick={() => setTradeType('grid')}>Grid</span>
                                </div>
                                <div className="spot-order-header-v2">
                                    <div className="spot-order-types-v2">
                                        <span className={`spot-order-type-v2 ${orderType === 'limit' ? 'active' : ''}`} onClick={() => setOrderType('limit')}>Limit</span>
                                        <span className={`spot-order-type-v2 ${orderType === 'market' ? 'active' : ''}`} onClick={() => setOrderType('market')}>Market</span>
                                        <span className={`spot-order-type-v2 ${orderType === 'stop' ? 'active' : ''}`} onClick={() => setOrderType('stop')}>Stop-limit</span>
                                        <Info size={14} className="text-zinc-500" />
                                    </div>
                                    <div className="spot-order-actions-v2">
                                        <button className="spot-action-pill-v2" onClick={() => navigate('/dashboard/deposit')}>Deposit</button>
                                        <button className="spot-action-pill-v2" onClick={() => navigate('/dashboard/withdraw')}>Withdraw</button>
                                    </div>
                                </div>
                                <div className="spot-trade-forms-grid-v2">
                                    <div className="spot-form-column-v2">
                                        <div className="spot-balance-row-v2"><span>Avbl</span><span className="balance-value">{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${userBalance.toFixed(2)} USDT`}</span></div>
                                        {orderType !== 'market' && (<div className="spot-input-group-v2">
                                            <span className="spot-input-label-v2">Price</span>
                                            <input type="text" inputMode="decimal" className="spot-input-field-v2" value={tradePrice} onChange={e => handleNumericInput(e.target.value, setTradePrice)} placeholder="0.00" />
                                            <button onClick={() => currentPrice > 0 && setTradePrice(currentPrice.toFixed(currentPrice >= 1000 ? 2 : currentPrice >= 1 ? 4 : 6))} title="Use current market rate" style={{ background: 'rgba(28,212,167,0.12)', border: '1px solid rgba(28,212,167,0.4)', color: '#1CD4A7', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0, lineHeight: 1.4 }}>MKT</button>
                                            <span className="spot-input-unit-v2">USDT</span>
                                        </div>)}
                                        <div className="spot-input-group-v2">
                                            <span className="spot-input-label-v2">Amount</span>
                                            <input type="text" inputMode="decimal" className="spot-input-field-v2" value={tradeAmount} onChange={e => handleAmountChange(e.target.value)} placeholder="0.00" />
                                            <span className="spot-input-unit-v2">{selectedCoin}</span>
                                        </div>
                                        {(() => {
                                            const pctVal = currentPrice > 0 && userBalance > 0 ? Math.min(100, Math.round((parseFloat(tradeAmount) || 0) * currentPrice / userBalance * 100)) : 0
                                            const setPct = (p: number) => { if (currentPrice > 0 && userBalance > 0) setTradeAmount(((userBalance * p / 100) / currentPrice).toFixed(6)); else if (currentPrice > 0) setTradeAmount('0.000000') }
                                            return (
                                            <div style={{ marginBottom: 10 }}>
                                                <div style={{ position: 'relative', height: 24, display: 'flex', alignItems: 'center' }}>
                                                    <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: 'var(--ui-bg-elevated)', borderRadius: 2 }}>
                                                        <div style={{ position: 'absolute', left: 0, width: `${pctVal}%`, height: '100%', background: '#00C0A3', borderRadius: 2 }} />
                                                        <div style={{ position: 'absolute', left: `${pctVal}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 14, height: 14, borderRadius: '50%', background: '#00C0A3', border: '2px solid #fff', boxShadow: '0 0 4px rgba(0,192,163,0.5)', pointerEvents: 'none', zIndex: 1 }} />
                                                        {[0,25,50,75,100].map(n => <div key={n} style={{ position: 'absolute', left: `${n}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 4, height: 4, borderRadius: '50%', background: pctVal >= n ? '#00C0A3' : '#3a3a4a', pointerEvents: 'none' }} />)}
                                                    </div>
                                                    <input type="range" min={0} max={100} step={1} value={pctVal}
                                                        style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer', margin: 0, padding: 0, zIndex: 2 }}
                                                        onChange={e => setPct(Number(e.target.value))} />
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                                                    {[0,25,50,75,100].map(n => <span key={n} onClick={() => setPct(n)} style={{ color: pctVal >= n ? '#00C0A3' : '#52525b', fontSize: 10, cursor: 'pointer' }}>{n}%</span>)}
                                                </div>
                                            </div>
                                            )
                                        })()}
                                        <div className="spot-input-group-v2">
                                            <span className="spot-input-label-v2">Total</span>
                                            <input type="text" inputMode="decimal" className="spot-input-field-v2" value={tradeTotal} onChange={e => handleTotalChange(e.target.value)} onFocus={() => { lastEditedField.current = 'total' }} placeholder="0.00" style={{ fontVariantNumeric: 'tabular-nums' }} />
                                            <span className="spot-input-unit-v2">USDT</span>
                                        </div>
                                        <button className="spot-main-btn buy" onClick={() => handleTrade('buy')} disabled={submittingSide !== null || currentPrice <= 0 || marketClosed}>{submittingSide === 'buy' ? 'Processing...' : marketClosed ? 'Market Closed' : currentPrice <= 0 ? 'Loading price...' : `Buy ${selectedCoin}`}</button>
                                    </div>
                                    <div className="spot-form-column-v2">
                                        <div className="spot-balance-row-v2"><span>Avbl</span><span className="balance-value">{balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `${userBalance.toFixed(2)} USDT`}</span></div>
                                        {orderType !== 'market' && (<div className="spot-input-group-v2">
                                            <span className="spot-input-label-v2">Price</span>
                                            <input type="text" inputMode="decimal" className="spot-input-field-v2" value={tradePrice} onChange={e => handleNumericInput(e.target.value, setTradePrice)} placeholder="0.00" />
                                            <button onClick={() => currentPrice > 0 && setTradePrice(currentPrice.toFixed(currentPrice >= 1000 ? 2 : currentPrice >= 1 ? 4 : 6))} title="Use current market rate" style={{ background: 'rgba(28,212,167,0.12)', border: '1px solid rgba(28,212,167,0.4)', color: '#1CD4A7', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0, lineHeight: 1.4 }}>MKT</button>
                                            <span className="spot-input-unit-v2">USDT</span>
                                        </div>)}
                                        <div className="spot-input-group-v2">
                                            <span className="spot-input-label-v2">Amount</span>
                                            <input type="text" inputMode="decimal" className="spot-input-field-v2" value={tradeAmount} onChange={e => handleAmountChange(e.target.value)} placeholder="0.00" />
                                            <span className="spot-input-unit-v2">{selectedCoin}</span>
                                        </div>
                                        {(() => {
                                            const pctVal = currentPrice > 0 && userBalance > 0 ? Math.min(100, Math.round((parseFloat(tradeAmount) || 0) * currentPrice / userBalance * 100)) : 0
                                            const setPct = (p: number) => { if (currentPrice > 0 && userBalance > 0) setTradeAmount(((userBalance * p / 100) / currentPrice).toFixed(6)); else if (currentPrice > 0) setTradeAmount('0.000000') }
                                            return (
                                            <div style={{ marginBottom: 10 }}>
                                                <div style={{ position: 'relative', height: 24, display: 'flex', alignItems: 'center' }}>
                                                    <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: 'var(--ui-bg-elevated)', borderRadius: 2 }}>
                                                        <div style={{ position: 'absolute', left: 0, width: `${pctVal}%`, height: '100%', background: '#00C0A3', borderRadius: 2 }} />
                                                        <div style={{ position: 'absolute', left: `${pctVal}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 14, height: 14, borderRadius: '50%', background: '#00C0A3', border: '2px solid #fff', boxShadow: '0 0 4px rgba(0,192,163,0.5)', pointerEvents: 'none', zIndex: 1 }} />
                                                        {[0,25,50,75,100].map(n => <div key={n} style={{ position: 'absolute', left: `${n}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 4, height: 4, borderRadius: '50%', background: pctVal >= n ? '#00C0A3' : '#3a3a4a', pointerEvents: 'none' }} />)}
                                                    </div>
                                                    <input type="range" min={0} max={100} step={1} value={pctVal}
                                                        style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer', margin: 0, padding: 0, zIndex: 2 }}
                                                        onChange={e => setPct(Number(e.target.value))} />
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                                                    {[0,25,50,75,100].map(n => <span key={n} onClick={() => setPct(n)} style={{ color: pctVal >= n ? '#00C0A3' : '#52525b', fontSize: 10, cursor: 'pointer' }}>{n}%</span>)}
                                                </div>
                                            </div>
                                            )
                                        })()}
                                        <div className="spot-input-group-v2">
                                            <span className="spot-input-label-v2">Total</span>
                                            <input type="text" inputMode="decimal" className="spot-input-field-v2" value={tradeTotal} onChange={e => handleTotalChange(e.target.value)} onFocus={() => { lastEditedField.current = 'total' }} placeholder="0.00" style={{ fontVariantNumeric: 'tabular-nums' }} />
                                            <span className="spot-input-unit-v2">USDT</span>
                                        </div>
                                        <button className="spot-main-btn sell" onClick={() => handleTrade('sell')} disabled={submittingSide !== null || currentPrice <= 0 || marketClosed}>{submittingSide === 'sell' ? 'Processing...' : marketClosed ? 'Market Closed' : currentPrice <= 0 ? 'Loading price...' : `Sell ${selectedCoin}`}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="spot-orderbook-panel-v2">
                            <div className="spot-ob-header-v2">
                                <div className="spot-ob-header-left"><div className="spot-ob-tabs-v2">
                                    <span className={`spot-ob-tab-v2 ${obTab === 'orderbooks' ? 'active' : ''}`} onClick={() => setObTab('orderbooks')}>Orderbooks</span>
                                    <span className={`spot-ob-tab-v2 ${obTab === 'trades' ? 'active' : ''}`} onClick={() => setObTab('trades')}>Last trades</span>
                                </div></div>
                                <div className="spot-ob-menu-btn"><div className="spot-ob-menu-dots"><span></span><span></span><span></span></div></div>
                            </div>
                            <div className="spot-ob-controls-v2">
                                <div className="spot-ob-view-btns-v2"><div className="spot-ob-view-btn-v2 both active"></div><div className="spot-ob-view-btn-v2 bids"></div><div className="spot-ob-view-btn-v2 asks"></div></div>
                                <div className="spot-ob-precision-v2">0.01 <ChevronDown size={10} /></div>
                            </div>
                            <div className="spot-ob-columns-v2"><span>Price(USDT)</span><span>Amount({selectedCoin})</span><span>Total</span></div>
                            <div className="spot-ob-list-v2 asks">
                                {askOrders.slice(0, 18).reverse().map((order, i) => (
                                    <div key={i} className="spot-ob-row-v2">
                                        <span className="price ask">{formatPrice(order.price)}</span>
                                        <span className="amount">{order.amount.toFixed(5)}</span>
                                        <span className="total">{order.total.toFixed(2)}</span>
                                        <div className="depth-bg-v2 ask" style={{ width: `${Math.min(order.total / (askOrders[0]?.total || 1) * 60, 100)}%` }}></div>
                                    </div>
                                ))}
                            </div>
                            <div className="spot-ob-spread-v2">
                                <span className="spot-ob-spread-price-v2">{formatPrice(currentPrice)}</span>
                                <span className="spot-ob-spread-arrow">{change24h >= 0 ? '\u2191' : '\u2193'}</span>
                                <span className="spot-ob-spread-fiat">\u2248${formatPrice(currentPrice)}</span>
                            </div>
                            <div className="spot-ob-list-v2 bids">
                                {bidOrders.slice(0, 12).map((order, i) => (
                                    <div key={i} className="spot-ob-row-v2">
                                        <span className="price bid">{formatPrice(order.price)}</span>
                                        <span className="amount">{order.amount.toFixed(5)}</span>
                                        <span className="total">{order.total.toFixed(2)}</span>
                                        <div className="depth-bg-v2 bid" style={{ width: `${Math.min(order.total / (bidOrders[0]?.total || 1) * 60, 100)}%` }}></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="spot-orders-panel">
                        <div className="spot-orders-header">
                            <div className="spot-orders-tabs">
                                <span className={`spot-orders-tab ${ordersTab === 'open' ? 'active' : ''}`} onClick={() => setOrdersTab('open')}>Open orders ({openTradesList.length})</span>
                                <span className={`spot-orders-tab ${ordersTab === 'history' ? 'active' : ''}`} onClick={() => setOrdersTab('history')}>Orders history</span>
                            </div>
                        </div>
                        {ordersTab === 'open' && openTradesList.length > 0 && (
                            <div style={{ padding: '8px 16px' }}>
                                <table style={{ width: '100%', fontSize: 12, color: 'var(--ui-text-secondary)' }}>
                                    <thead><tr style={{ borderBottom: '1px solid #1a1a24' }}>
                                        <th style={{ textAlign: 'left', padding: '6px 0', fontWeight: 500 }}>Pair</th>
                                        <th style={{ textAlign: 'left', padding: '6px 0', fontWeight: 500 }}>Side</th>
                                        <th style={{ textAlign: 'left', padding: '6px 0', fontWeight: 500 }}>Status</th>
                                        <th style={{ textAlign: 'right', padding: '6px 0', fontWeight: 500 }}>Amount</th>
                                        <th style={{ textAlign: 'right', padding: '6px 0', fontWeight: 500 }}>Entry</th>
                                        <th style={{ textAlign: 'right', padding: '6px 0', fontWeight: 500 }}>P&amp;L</th>
                                        <th style={{ textAlign: 'right', padding: '6px 0', fontWeight: 500 }}>Action</th>
                                    </tr></thead>
                                    <tbody>{openTradesList.map(trade => (
                                        <tr key={trade._id} style={{ borderBottom: '1px solid #0f0f14' }}>
                                            <td style={{ padding: '6px 0', color: 'var(--ui-text-primary)' }}>{trade.asset}/USDT</td>
                                            <td style={{ padding: '6px 0', color: trade.side === 'buy' ? '#10b981' : '#ef4444' }}>{trade.side.toUpperCase()}</td>
                                            <td style={{ padding: '6px 0' }}>
                                                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: trade.status === 'pending' ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)', color: trade.status === 'pending' ? '#F59E0B' : '#10b981' }}>
                                                    {trade.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '6px 0' }}>{trade.amount}</td>
                                            <td style={{ textAlign: 'right', padding: '6px 0' }}>${formatPrice(trade.entryPrice)}</td>
                                            <td style={{ textAlign: 'right', padding: '6px 0', color: computeLivePnL(trade) >= 0 ? '#10b981' : '#ef4444', transition: 'color 0.3s ease', fontVariantNumeric: 'tabular-nums' }}>
                                                {trade.status === 'pending' ? '—' : `${computeLivePnL(trade) >= 0 ? '+' : ''}$${computeLivePnL(trade).toFixed(2)}`}
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '6px 0' }}>
                                                <button onClick={() => handleCloseTrade(trade._id)} style={{ background: trade.status === 'pending' ? '#F59E0B' : '#ef4444', color: 'var(--ui-text-primary)', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>
                                                    {trade.status === 'pending' ? 'Cancel' : 'Close'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </div>
                        )}
                        {ordersTab === 'open' && openTradesList.length === 0 && (
                            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ui-text-muted)', fontSize: 13 }}>No open orders</div>
                        )}
                    </div>
                </div>
            </div>
            <SettingsSidebar isOpen={showSettingsSidebar} onClose={() => setShowSettingsSidebar(false)} />
        </Layout>
    )
}

